// src/App.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet, HelmetProvider } from "react-helmet-async";
import TaskOptimizer from "./TaskOptimizer";
import ToolsPage from "./pages/ToolsPage";
import PDFEditorPage from "./pages/PDFEditorPage";
import ImageConverterPage from "./pages/ImageConverterPage";
import JsonFormatterPage from "./pages/JsonFormatterPage";
import TextCaseConverterPage from "./pages/TextCaseConverterPage";
import PasswordGeneratorPage from "./pages/PasswordGeneratorPage";
import Base64ToolPage from "./pages/Base64ToolPage";
import PdfToolkitPage from "./pages/PdfToolkitPage";
import BgUpscalePage from "./pages/BgUpscalePage";
import OcrToolPage from "./pages/OcrToolPage";
import ResumeAtsPage from "./pages/ResumeAtsPage";
import InvoiceGeneratorPage from "./pages/InvoiceGeneratorPage";
import EmailRewritePage from "./pages/EmailRewritePage";
import GrammarPlagiarismPage from "./pages/GrammarPlagiarismPage";
import UrlToolkitPage from "./pages/UrlToolkitPage";
import ZipUnzipPage from "./pages/ZipUnzipPage";
import QrBarcodePage from "./pages/QrBarcodePage";
import './App.css';

/** -----------------------------------------------------------------------------
 *  ENV / API base
 *  - Same-origin in prod
 *  - http://localhost:5000 for local dev (common dev ports handled)
 * --------------------------------------------------------------------------- */
const isLocalDev = () => {
  if (typeof window === 'undefined') return false;
  const { hostname, port } = window.location;
  const devHosts = new Set(['localhost', '127.0.0.1', '::1']);
  return devHosts.has(hostname) || ['3000', '5173'].includes(port);
};

// Prefer SAME-ORIGIN in production regardless of an accidentally-set env var
const computeApiBase = () => {
  if (typeof window === 'undefined') return '';
  const { hostname } = window.location;
  const devHosts = new Set(['localhost', '127.0.0.1', '::1']);
  const envBase = process.env.REACT_APP_API_BASE;
  // If not on a dev host, force same-origin (empty string) even if env is set
  if (!devHosts.has(hostname)) return '';
  // Local dev host: use explicit env if provided, otherwise use relative URLs so
  // the CRA dev-server proxy handles them (avoids CORS when on 127.0.0.1:3000).
  return envBase || '';
};

const API_BASE = computeApiBase();

/** -----------------------------------------------------------------------------
 *  Meta helpers
 * --------------------------------------------------------------------------- */
const setMetaDescription = (content) => {
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'description';
    document.head.appendChild(meta);
  }
  meta.content = content;
};

/** -----------------------------------------------------------------------------
 *  Small UI helpers
 * --------------------------------------------------------------------------- */
const FlickeringSeparator = () => (
  <>
    <style>
      {`
        @keyframes flicker {
          0%, 18%, 22%, 25%, 53%, 57%, 100% {
            opacity: 0.4;
            box-shadow: 0 0 2px rgba(0, 221, 255, 0.1), 0 0 5px rgba(0, 221, 255, 0.1);
          }
          20%, 24%, 55% {
            opacity: 1;
            box-shadow: 0 0 5px rgba(0, 221, 255, 0.2), 0 0 15px rgba(0, 221, 255, 0.2);
          }
        }
        .flicker-line { animation: flicker 4s infinite linear; }
      `}
    </style>
    <div className="w-full h-[1px] bg-cyan-800/70 flicker-line" />
  </>
);

// ============================================================
// ANIMATION UTILITIES – inspired by modern portfolio design
// ============================================================

/** Thin glowing scroll-progress bar pinned at the top of the page */
const ScrollProgressBar = () => {
  const [progress, setProgress] = React.useState(0);
  React.useEffect(() => {
    const update = () => {
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docH > 0 ? (window.scrollY / docH) * 100 : 0);
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
    return () => window.removeEventListener('scroll', update);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[9998] h-[2px] bg-transparent pointer-events-none">
      <div
        style={{
          width: `${progress}%`,
          transition: 'width 0.08s linear',
          boxShadow: '0 0 8px #00DDFF, 0 0 18px rgba(0,221,255,0.5)',
        }}
        className="h-full bg-gradient-to-r from-cyan-400 via-purple-500 to-cyan-400"
      />
    </div>
  );
};

/** ─────────────────────────────────────────────────────────────────────────
 *  FUTURISTIC CURSOR
 *  • Dot that snaps instantly to mouse position
 *  • Lagged outer ring that trails behind
 *  • Dashed rotating second ring when hovering interactive elements
 *  • Crosshair corner ticks that appear on hover
 *  • Plasma glow + click ripple burst
 *  • 6-particle comet trail
 * ──────────────────────────────────────────────────────────────────────── */
const CustomCursor = () => {
  const [pos,    setPos]    = React.useState({ x: -300, y: -300 });
  const [trail,  setTrail]  = React.useState({ x: -300, y: -300 });
  const [ring2,  setRing2]  = React.useState({ x: -300, y: -300 });
  const [hov,      setHov]      = React.useState(false);
  const [overText, setOverText] = React.useState(false);
  const [clicks,   setClicks]   = React.useState([]);
  const [particles, setParticles] = React.useState([]);

  const rawPos  = useRef({ x: -300, y: -300 });
  const rafRef  = useRef(null);

  React.useEffect(() => {
    const TEXT_SEL = 'p,h1,h2,h3,h4,h5,h6,span,li,td,th,label,blockquote,small,strong,em,b,i,cite,mark';
    const onMove = (e) => {
      const nx = e.clientX, ny = e.clientY;
      setPos({ x: nx, y: ny });
      rawPos.current = { x: nx, y: ny };
      // Dim glow when cursor is over readable text (not interactive elements)
      const el = document.elementFromPoint(nx, ny);
      if (el) {
        const iact = el.closest('a,button,[role="button"],input,textarea,select,[tabindex]');
        setOverText(!iact && !!el.closest(TEXT_SEL));
      }
      // Spawn a comet particle every ~4 pixels moved
      setParticles(prev => {
        const next = [
          ...prev.slice(-9),
          { id: Date.now() + Math.random(), x: nx, y: ny, born: Date.now() },
        ];
        return next;
      });
    };

    const onClick = (e) => {
      const id = Date.now();
      setClicks(prev => [...prev.slice(-4), { id, x: e.clientX, y: e.clientY }]);
      setTimeout(() => setClicks(prev => prev.filter(c => c.id !== id)), 700);
    };

    const setT = () => setHov(true);
    const setF = () => setHov(false);

    document.addEventListener('mousemove', onMove);
    document.addEventListener('click', onClick);

    const attach = () => {
      document.querySelectorAll('a,button,[role="button"],input,textarea,select').forEach(el => {
        el.addEventListener('mouseenter', setT);
        el.addEventListener('mouseleave', setF);
      });
    };
    attach();
    const obs = new MutationObserver(attach);
    obs.observe(document.body, { childList: true, subtree: true });

    // Two separate lag speeds for trail vs ring2
    let tx = -300, ty = -300, rx = -300, ry = -300;
    const animate = () => {
      const { x, y } = rawPos.current;
      tx += (x - tx) * 0.13;
      ty += (y - ty) * 0.13;
      rx += (x - rx) * 0.07;
      ry += (y - ry) * 0.07;
      setTrail({ x: tx, y: ty });
      setRing2({ x: rx, y: ry });
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('click', onClick);
      cancelAnimationFrame(rafRef.current);
      obs.disconnect();
    };
  }, []);

  // Clean up stale particles
  React.useEffect(() => {
    const t = setTimeout(() => {
      const now = Date.now();
      setParticles(prev => prev.filter(p => now - p.born < 400));
    }, 420);
    return () => clearTimeout(t);
  }, [particles]);

  return (
    <>
      <style>{`
        @media (hover:hover) and (pointer:fine){ body:not(.native-cursor) *{ cursor: none !important; } }
        @keyframes cursor-ripple {
          0%   { transform: translate(-50%,-50%) scale(0); opacity: 0.9; }
          100% { transform: translate(-50%,-50%) scale(4.5); opacity: 0; }
        }
        @keyframes cursor-rotate { to { transform: translate(-50%,-50%) rotate(360deg); } }
        @keyframes cursor-rotate-r { to { transform: translate(-50%,-50%) rotate(-360deg); } }
        @keyframes particle-fade {
          0%   { opacity: 0.85; transform: translate(-50%,-50%) scale(1); }
          100% { opacity: 0;    transform: translate(-50%,-50%) scale(0); }
        }
        @keyframes dot-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 0px rgba(255,255,255,0.9),
              0 0 6px  3px rgba(0,221,255,1),
              0 0 16px 6px rgba(0,221,255,0.7),
              0 0 32px 10px rgba(0,221,255,0.35),
              0 0 56px 18px rgba(168,139,250,0.2);
          }
          50% {
            box-shadow:
              0 0 0 2px rgba(255,255,255,0.5),
              0 0 10px 5px rgba(0,221,255,1),
              0 0 24px 10px rgba(0,221,255,0.8),
              0 0 48px 18px rgba(0,221,255,0.45),
              0 0 80px 28px rgba(168,139,250,0.3);
          }
        }
        @keyframes dot-pulse-hover {
          0%, 100% {
            box-shadow:
              0 0 0 3px rgba(255,255,255,0.6),
              0 0 10px 4px rgba(255,255,255,0.9),
              0 0 22px 8px rgba(0,221,255,1),
              0 0 44px 16px rgba(0,221,255,0.6),
              0 0 70px 24px rgba(168,139,250,0.4);
          }
          50% {
            box-shadow:
              0 0 0 5px rgba(255,255,255,0.3),
              0 0 16px 7px rgba(255,255,255,1),
              0 0 34px 14px rgba(0,221,255,0.9),
              0 0 64px 24px rgba(0,221,255,0.5),
              0 0 100px 36px rgba(168,139,250,0.35);
          }
        }
        @keyframes satellite-spin {
          to { transform: translate(-50%,-50%) rotate(360deg); }
        }
      `}</style>

      {/* ── Comet trail particles ── */}
      {particles.map((p, i) => (
        <div
          key={p.id}
          className="fixed pointer-events-none z-[9996] rounded-full"
          style={{
            left: p.x, top: p.y,
            width: i % 3 === 0 ? '5px' : '3px',
            height: i % 3 === 0 ? '5px' : '3px',
            background: i % 2 === 0
              ? 'radial-gradient(circle, rgba(0,221,255,1), rgba(0,221,255,0))'
              : 'radial-gradient(circle, rgba(200,180,255,1), rgba(168,139,250,0))',
            boxShadow: i % 2 === 0
              ? '0 0 6px 2px rgba(0,221,255,0.6)'
              : '0 0 6px 2px rgba(168,139,250,0.5)',
            animation: 'particle-fade 0.45s ease-out forwards',
          }}
        />
      ))}

      {/* ── Click ripple bursts ── */}
      {clicks.map((c) => (
        <div
          key={c.id}
          className="fixed pointer-events-none z-[9997]"
          style={{
            left: c.x, top: c.y,
            width: '32px', height: '32px',
            borderRadius: '50%',
            border: '1.5px solid rgba(0,221,255,0.8)',
            animation: 'cursor-ripple 0.65s cubic-bezier(0.25,0.46,0.45,0.94) forwards',
            boxShadow: '0 0 12px rgba(0,221,255,0.5)',
          }}
        />
      ))}

      {/* ── Slow outer ghost ring (ring2 — most lag) ── */}
      <div
        className="fixed pointer-events-none z-[9997]"
        style={{
          left: ring2.x, top: ring2.y,
          width: hov ? '68px' : '48px',
          height: hov ? '68px' : '48px',
          borderRadius: '50%',
          border: `1px solid rgba(168,139,250,${hov ? 0.3 : 0.15})`,
          transform: 'translate(-50%,-50%)',
          background: hov ? 'radial-gradient(circle, rgba(168,139,250,0.04), transparent)' : 'transparent',
          transition: 'width 0.35s, height 0.35s, border-color 0.35s, background 0.35s',
        }}
      />

      {/* ── Mid trail ring (trail — medium lag) ── */}
      <div
        className="fixed pointer-events-none z-[9998]"
        style={{
          left: trail.x, top: trail.y,
          width: hov ? '44px' : '30px',
          height: hov ? '44px' : '30px',
          borderRadius: '50%',
          border: `1.5px solid rgba(0,221,255,${hov ? 0.75 : 0.45})`,
          transform: 'translate(-50%,-50%)',
          boxShadow: hov
            ? '0 0 18px rgba(0,221,255,0.5), inset 0 0 10px rgba(0,221,255,0.08)'
            : '0 0 8px rgba(0,221,255,0.2)',
          background: hov ? 'rgba(0,221,255,0.05)' : 'transparent',
          transition: 'width 0.22s, height 0.22s, border-color 0.22s, box-shadow 0.22s',
        }}
      />

      {/* ── Rotating dashed ring (only on hover) ── */}
      {hov && (
        <div
          className="fixed pointer-events-none z-[9998]"
          style={{
            left: trail.x, top: trail.y,
            width: '60px', height: '60px',
            borderRadius: '50%',
            border: '1px dashed rgba(0,221,255,0.35)',
            animation: 'cursor-rotate 2s linear infinite',
          }}
        />
      )}

      {/* ── Crosshair ticks (4 corner ticks on hover) ── */}
      {hov && [0, 90, 180, 270].map((deg) => (
        <div
          key={deg}
          className="fixed pointer-events-none z-[9998]"
          style={{
            left: trail.x, top: trail.y,
            width: '8px', height: '1.5px',
            background: 'rgba(0,221,255,0.6)',
            transform: `translate(-50%,-50%) rotate(${deg}deg) translateX(26px)`,
            transition: 'opacity 0.2s',
          }}
        />
      ))}

      {/* ── Satellite micro-ring orbiting the dot ── */}
      <div
        className="fixed pointer-events-none z-[9998]"
        style={{
          left: pos.x, top: pos.y,
          width: hov ? '22px' : '16px',
          height: hov ? '22px' : '16px',
          borderRadius: '50%',
          border: `1px solid rgba(0,221,255,${hov ? 0.9 : 0.6})`,
          animation: 'satellite-spin 1.2s linear infinite',
          backgroundImage: `conic-gradient(rgba(0,221,255,0.6) 0deg, transparent 90deg, transparent 360deg)`,
          transition: 'width 0.18s, height 0.18s, opacity 0.2s',
          opacity: overText ? 0 : 1,
        }}
      />

      {/* ── Plasma dot core (snaps to exact mouse pos) ── */}
      <div
        className="fixed pointer-events-none z-[9999]"
        style={{
          left: pos.x, top: pos.y,
          width: hov ? '8px' : overText ? '5px' : '9px',
          height: hov ? '8px' : overText ? '5px' : '9px',
          borderRadius: '50%',
          background: hov
            ? 'radial-gradient(circle at 35% 35%, #ffffff, #00DDFF 50%, rgba(168,139,250,0.8))'
            : 'radial-gradient(circle at 35% 35%, #ffffff, #00DDFF 55%, rgba(0,170,204,0.6))',
          transform: 'translate(-50%,-50%)',
          // Over text: kill the ambient halo, keep only a tiny pin-point glow
          animation: overText ? 'none' : hov ? 'dot-pulse-hover 0.9s ease-in-out infinite' : 'dot-pulse 1.6s ease-in-out infinite',
          boxShadow: overText
            ? '0 0 3px 1px rgba(0,221,255,0.35)'
            : undefined,
          transition: 'width 0.15s, height 0.15s, background 0.2s, box-shadow 0.2s',
        }}
      />

      {/* ── Hot specular highlight on the dot ── */}
      <div
        className="fixed pointer-events-none z-[9999]"
        style={{
          left: pos.x,
          top: pos.y,
          width: '3px',
          height: '3px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.95)',
          transform: 'translate(calc(-50% - 1.5px), calc(-50% - 1.5px))',
          boxShadow: '0 0 4px 1px rgba(255,255,255,0.9)',
        }}
      />
    </>
  );
};

/** Typewriter: reveals text character by character with a blinking cursor */
const TypewriterText = ({ text, className = '', speed = 45 }) => {
  const [displayed, setDisplayed] = React.useState('');
  const [done, setDone] = React.useState(false);
  React.useEffect(() => {
    setDisplayed(''); setDone(false);
    let i = 0;
    const t = setInterval(() => { i++; setDisplayed(text.slice(0, i)); if (i >= text.length) { clearInterval(t); setDone(true); } }, speed);
    return () => clearInterval(t);
  }, [text, speed]);
  return (
    <span className={className}>
      {displayed}
      {!done && <span className="text-cyan-400 animate-pulse ml-0.5">|</span>}
    </span>
  );
};

/** GlitchText – text that occasionally glitches with RGB-split offsets */
const GlitchText = ({ text, className = '', intensity = 1 }) => {
  const [glitching, setGlitching] = React.useState(false);
  const [offset, setOffset] = React.useState({ r: 0, b: 0 });
  React.useEffect(() => {
    const trigger = () => {
      if (Math.random() > 0.6) return;
      setGlitching(true);
      setOffset({ r: (Math.random() - 0.5) * 6 * intensity, b: (Math.random() - 0.5) * 6 * intensity });
      const frames = [80, 140, 60, 100];
      let idx = 0;
      const next = () => {
        idx++;
        if (idx < frames.length) {
          setOffset({ r: (Math.random() - 0.5) * 6 * intensity, b: (Math.random() - 0.5) * 6 * intensity });
          setTimeout(next, frames[idx]);
        } else {
          setGlitching(false);
          setOffset({ r: 0, b: 0 });
        }
      };
      setTimeout(next, frames[0]);
    };
    const id = setInterval(trigger, 2500 + Math.random() * 3000);
    return () => clearInterval(id);
  }, [intensity]);
  return (
    <span className={`relative inline-block ${className}`}>
      {/* Red channel */}
      <span
        aria-hidden="true"
        className="absolute inset-0 select-none"
        style={{
          transform: glitching ? `translate(${offset.r}px, 0)` : 'none',
          color: 'rgba(255,50,50,0.7)',
          clipPath: glitching ? 'inset(25% 0 50% 0)' : 'none',
          transition: 'transform 0.05s',
        }}
      >{text}</span>
      {/* Blue channel */}
      <span
        aria-hidden="true"
        className="absolute inset-0 select-none"
        style={{
          transform: glitching ? `translate(${offset.b}px, 0)` : 'none',
          color: 'rgba(0,221,255,0.7)',
          clipPath: glitching ? 'inset(50% 0 25% 0)' : 'none',
          transition: 'transform 0.05s',
        }}
      >{text}</span>
      {/* Main text on top */}
      <span className="relative">{text}</span>
    </span>
  );
};

/** SplitRevealText – words fly in one-by-one from below on mount */
const SplitRevealText = ({ text, className = '', wordClass = '', delay = 0, stagger = 80 }) => {
  const [visible, setVisible] = React.useState(false);
  const ref = useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const words = text.split(' ');
  return (
    <span ref={ref} className={`inline ${className}`} aria-label={text}>
      {words.map((word, i) => (
        <span
          key={i}
          className={`inline-block ${i < words.length - 1 ? 'mr-[0.28em]' : ''} ${wordClass}`}
          style={{
            clipPath: visible ? 'inset(0 0 -30% 0)' : 'inset(0 0 100% 0)',
            transform: visible ? 'translateY(0)' : 'translateY(60%)',
            opacity: visible ? 1 : 0,
            transition: `clip-path 0.55s cubic-bezier(0.16,1,0.3,1) ${delay + i * stagger}ms, transform 0.55s cubic-bezier(0.16,1,0.3,1) ${delay + i * stagger}ms, opacity 0.4s ease ${delay + i * stagger}ms`,
            verticalAlign: 'baseline',
          }}
        >
          {word}
        </span>
      ))}
    </span>
  );
};

/** CountUpNumber – animates from 0 to the target number on scroll-reveal */
const CountUpNumber = ({ value, suffix = '', prefix = '', duration = 1800, className = '' }) => {
  const [count, setCount] = React.useState(0);
  const [started, setStarted] = React.useState(false);
  const ref = useRef(null);
  // Parse numeric part
  const numStr = String(value).replace(/[^0-9.]/g, '');
  const num = parseFloat(numStr) || 0;
  const isFloat = numStr.includes('.');
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) { setStarted(true); obs.disconnect(); } },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [started]);
  React.useEffect(() => {
    if (!started) return;
    const start = performance.now();
    const raf = requestAnimationFrame(function tick(now) {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(eased * num);
      if (progress < 1) requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, [started, num, duration]);
  const display = isFloat ? count.toFixed(1) : Math.round(count).toLocaleString();
  return (
    <span ref={ref} className={className}>
      {prefix}{display}{suffix}
    </span>
  );
};

/** NeonFlicker – text that flickers like a neon sign turning on */
const NeonFlicker = ({ text, color = '#00DDFF', className = '' }) => {
  const [lit, setLit] = React.useState(false);
  const [flicker, setFlicker] = React.useState(false);
  const ref = useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          obs.disconnect();
          const seq = [120, 80, 200, 60, 350, 80, 150];
          let i = 0;
          const next = () => {
            setFlicker(v => !v);
            i++;
            if (i < seq.length) setTimeout(next, seq[i]);
            else { setFlicker(false); setLit(true); }
          };
          setTimeout(next, 300);
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <span
      ref={ref}
      className={className}
      style={{
        display: 'inline',
        verticalAlign: 'baseline',
        lineHeight: 'inherit',
        color: lit || flicker ? color : 'rgba(255,255,255,0.15)',
        textShadow: lit
          ? `0 0 7px ${color}, 0 0 20px ${color}, 0 0 42px ${color}80`
          : flicker
          ? `0 0 4px ${color}80`
          : 'none',
        transition: 'color 0.08s, text-shadow 0.08s',
      }}
    >
      {text}
    </span>
  );
};

/** IntersectionObserver hook – fires once when element enters viewport */
const useScrollReveal = (options = {}) => {
  const ref = useRef(null);
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: options.threshold ?? 0.08, rootMargin: options.rootMargin ?? '0px 0px -30px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

/** 3-D perspective tilt card – accepts any className, behaves like a div */
const TiltCard = ({ children, className = '', intensity = 8 }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${-y * intensity}deg) rotateY(${x * intensity}deg) scale3d(1.02,1.02,1.02)`;
    el.style.transition = 'transform 0.08s ease-out';
  };
  const onLeave = () => {
    const el = ref.current; if (!el) return;
    el.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
    el.style.transition = 'transform 0.55s cubic-bezier(0.23,1,0.32,1)';
  };
  return <div ref={ref} className={className} onMouseMove={onMove} onMouseLeave={onLeave}>{children}</div>;
};

/** Magnetic pull wrapper – children drift slightly toward cursor on hover */
const MagneticBtn = ({ children, className = '' }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.transform = `translate(${((e.clientX - r.left) / r.width - 0.5) * 18}px,${((e.clientY - r.top) / r.height - 0.5) * 9}px)`;
    el.style.transition = 'transform 0.1s ease';
  };
  const onLeave = () => {
    const el = ref.current; if (!el) return;
    el.style.transform = 'translate(0,0)';
    el.style.transition = 'transform 0.55s cubic-bezier(0.23,1,0.32,1)';
  };
  return <span ref={ref} className={`inline-block ${className}`} onMouseMove={onMove} onMouseLeave={onLeave}>{children}</span>;
};

/** Infinite-scroll marquee ticker – pauses on hover */
const Marquee = ({ items = [] }) => {
  const dup = [...items, ...items];
  return (
    <div className="w-full overflow-hidden border-y border-cyan-400/10 py-3 mt-10 select-none">
      <div
        className="flex whitespace-nowrap"
        style={{ animation: 'marquee-scroll 24s linear infinite' }}
        onMouseEnter={e => (e.currentTarget.style.animationPlayState = 'paused')}
        onMouseLeave={e => (e.currentTarget.style.animationPlayState = 'running')}
      >
        {dup.map((item, i) => (
          <span key={i} className="mx-8 text-cyan-400/60 font-mono text-xs tracking-[0.2em] uppercase">
            <span className="mr-4 text-purple-400/40">✦</span>{item}
          </span>
        ))}
      </div>
      <style>{`@keyframes marquee-scroll { from { transform: translateX(0) } to { transform: translateX(-50%) } }`}</style>
    </div>
  );
};

// ============================================================

// ---------------- ShimmerCardButton (clickable card + popup) ----------------
export const ShimmerCardButton = ({ 
  title, 
  dateTime,
  description, 
  icon, 
  onClick, 
  loadingTime = 2000,
  className = "",
  popupContent,
  ...props 
}) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isClicked, setIsClicked] = React.useState(false);
  const [showPopup, setShowPopup] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, loadingTime);
    return () => clearTimeout(timer);
  }, [loadingTime]);

  const handleClick = (e) => {
    if (isLoading) return;
    setIsClicked(true);
    setTimeout(() => {
      setShowPopup(true);
      setIsClicked(false);
    }, 400);
    onClick?.(e);
  };

  const closePopup = () => setShowPopup(false);

  if (isLoading) {
    return (
      <div className={`relative overflow-hidden bg-gray-900 border border-cyan-500/30 rounded-lg p-6 w-full max-w-sm ${className}`}>
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full bg-gradient-to-br from-cyan-500 to-purple-600"></div>
          <div className="absolute inset-0 bg-cyber-grid"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent animate-cyber-shimmer"></div>
        <div className="relative space-y-4">
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-400/20 to-purple-500/20 rounded-full border border-cyan-400/30 animate-pulse-glow"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gradient-to-r from-cyan-400/20 to-transparent rounded-full w-3/4 animate-pulse-glow"></div>
            <div className="h-4 bg-gradient-to-r from-purple-500/20 to-transparent rounded-full w-1/2 animate-pulse-glow"></div>
          </div>
          <div className="space-y-2">
            <div className="h-3 bg-gradient-to-r from-blue-400/20 to-transparent rounded-full w-full animate-pulse-glow"></div>
            <div className="h-3 bg-gradient-to-r from-cyan-400/20 to-transparent rounded-full w-5/6 animate-pulse-glow"></div>
            <div className="h-3 bg-gradient-to-r from-purple-400/20 to-transparent rounded-full w-4/6 animate-pulse-glow"></div>
          </div>
        </div>
        <style jsx>{`
          @keyframes cyber-shimmer {
            0% { transform: translateX(-100%); background: linear-gradient(90deg,transparent,rgba(0,255,255,.4),transparent) }
            50% { background: linear-gradient(90deg,transparent,rgba(138,43,226,.4),transparent) }
            100% { transform: translateX(100%); background: linear-gradient(90deg,transparent,rgba(0,255,255,.4),transparent) }
          }
          @keyframes pulse-glow {
            0%,100% { opacity:.3; box-shadow:0 0 5px rgba(0,255,255,.2) }
            50% { opacity:.7; box-shadow:0 0 15px rgba(0,255,255,.4), 0 0 25px rgba(138,43,226,.2) }
          }
          .animate-cyber-shimmer { animation: cyber-shimmer 2s infinite; }
          .animate-pulse-glow { animation: pulse-glow 2s infinite ease-in-out; }
          .bg-cyber-grid {
            background-image:
              linear-gradient(rgba(0,255,255,.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,255,255,.1) 1px, transparent 1px);
            background-size: 20px 20px;
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={handleClick}
        className={`
          group relative overflow-hidden bg-gray-900/95 backdrop-blur-sm rounded-lg p-6 w-full max-w-sm
          hover:bg-gray-800/95 focus:outline-none
          transition-all duration-400 ease-out transform hover:-translate-y-2
          ${isClicked ? 'border-2 border-cyan-400 shadow-cyber-active scale-98' : 'border border-cyan-500/40 hover:border-cyan-400/80 shadow-cyber'}
          ${className}
        `}
        {...props}
      >
        <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
          <div className="w-full h-full bg-gradient-to-br from-cyan-500 to-purple-600"></div>
          <div className="absolute inset-0 bg-cyber-grid"></div>
        </div>

        <div className="absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 border-cyan-400 opacity-60"></div>
        <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-cyan-400 opacity-60"></div>
        <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-purple-400 opacity-60"></div>
        <div className="absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 border-purple-400 opacity-60"></div>

        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-400/10 to-transparent h-1 animate-scan"></div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/0 via-cyan-400/5 to-purple-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        <div className="relative z-10 text-left space-y-4">
        {dateTime && (
            <p className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors duration-300 leading-relaxed">
              {dateTime}
            </p>
          )}
          {icon && (
            <div className="relative w-12 h-12 bg-gradient-to-br from-cyan-400/10 to-purple-500/10 rounded border border-cyan-400/30 flex items-center justify-center group-hover:border-cyan-400/60 transition-all duration-300">
              <span className="text-cyan-400 text-xl drop-shadow-glow">{icon}</span>
              <div className="absolute inset-0 rounded bg-gradient-to-br from-cyan-400/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          )}
          {title && (
            <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 group-hover:from-cyan-300 group-hover:to-purple-300 transition-all duration-300 drop-shadow-glow">
              {title}
            </h3>
          )}
          {description && (
            <p className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors duration-300 leading-relaxed">
              {description}
            </p>
          )}
        </div>

        <div className="absolute bottom-4 right-4 text-cyan-400/60 group-hover:text-cyan-400 transform translate-x-0 group-hover:translate-x-1 transition-all duration-300">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          <div className="absolute -inset-1 bg-cyan-400/20 rounded opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-300"></div>
        </div>

        <div className="absolute top-3 right-3">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse-slow shadow-green-glow"></div>
        </div>

        <style jsx>{`
          @keyframes scan {
            0% { top:0%; opacity:0 }
            50% { opacity:1 }
            100% { top:100%; opacity:0 }
          }
          @keyframes pulse-slow { 0%,100%{opacity:.4} 50%{opacity:1} }
          .animate-scan { animation: scan 2s ease-in-out infinite; }
          .animate-pulse-slow { animation: pulse-slow 3s ease-in-out infinite; }
          .shadow-cyber {
            box-shadow: 0 0 10px rgba(0,255,255,.1), 0 4px 20px rgba(0,0,0,.3), inset 0 1px 0 rgba(255,255,255,.05);
          }
          .shadow-cyber-active {
            box-shadow: 0 0 20px rgba(0,255,255,.3), 0 0 40px rgba(138,43,226,.2), 0 8px 32px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.1);
          }
          .shadow-green-glow { box-shadow: 0 0 10px rgba(34,197,94,.5); }
          .drop-shadow-glow { filter: drop-shadow(0 0 4px rgba(0,255,255,.3)); }
          .scale-98 { transform: scale(.98) translateY(-8px); }
          .bg-cyber-grid {
            background-image:
              linear-gradient(rgba(0,255,255,.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,255,255,.1) 1px, transparent 1px);
            background-size: 20px 20px;
          }
        `}</style>
      </button>

      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md animate-fade-in" onClick={closePopup}>
            <div className="absolute inset-0 bg-cyber-grid opacity-5"></div>
          </div>
          
          <div className="relative bg-gray-900/95 backdrop-blur-xl border-2 border-cyan-400/60 rounded-xl shadow-cyber-modal max-w-md w-full mx-4 animate-scale-in overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 via-purple-500 to-cyan-400 animate-pulse"></div>
            <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-cyan-400"></div>
            <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-cyan-400"></div>
            <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-purple-400"></div>
            <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-purple-400"></div>

            <button onClick={closePopup} className="absolute top-4 right-4 text-cyan-400/60 hover:text-cyan-400 transition-colors z-10 p-1 rounded hover:bg-cyan-400/10">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="p-8">
              <div className="flex items-center space-x-4 mb-6">
                {icon && (
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400/20 to-purple-500/20 rounded border border-cyan-400/40 flex items-center justify-center">
                    <span className="text-cyan-400 text-2xl drop-shadow-glow">{icon}</span>
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 mb-2 drop-shadow-glow">
                    {title}
                  </h2>
                  <p className="text-gray-300 text-sm">{description}</p>
                </div>
              </div>

              <div className="space-y-4">
                {popupContent ? (
                  <div className="cyber-content">{popupContent}</div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-gray-800/50 to-gray-700/50 backdrop-blur-sm rounded-lg p-4 border border-cyan-400/20">
                      <h3 className="font-bold text-cyan-400 mb-2 flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                        SYSTEM STATUS
                      </h3>
                      <p className="text-gray-300 text-sm font-mono">
                        Module: {title} | Status: ACTIVE | Security: ENCRYPTED
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-cyan-900/20 to-purple-900/20 backdrop-blur-sm rounded-lg p-4 border border-purple-400/20">
                      <h3 className="font-bold text-purple-400 mb-2 flex items-center">
                        <span className="w-2 h-2 bg-purple-400 rounded-full mr-2 animate-pulse"></span>
                        FEATURES
                      </h3>
                      <ul className="text-cyan-300 text-sm space-y-1 font-mono">
                        <li>→ Neural interface compatible</li>
                        <li>→ Quantum-encrypted data streams</li>
                        <li>→ Real-time holographic display</li>
                        <li>→ Biometric security protocols</li>
                      </ul>
                    </div>
                  </div>
                )}
                <div className="flex space-x-3 pt-4">
                  <button onClick={closePopup} className="flex-1 bg-gradient-to-r from-cyan-600 to-cyan-500 text-white py-3 px-4 rounded-lg hover:from-cyan-500 hover:to-cyan-400 transition-all duration-300 font-bold shadow-cyber border border-cyan-400/50">
                    INITIALIZE
                  </button>
                  <button onClick={closePopup} className="flex-1 bg-gradient-to-r from-gray-700 to-gray-600 text-gray-200 py-3 px-4 rounded-lg hover:from-gray-600 hover:to-gray-500 transition-all duration-300 font-bold border border-gray-500/50">
                    ABORT
                  </button>
                </div>
              </div>
            </div>

            <style jsx>{`
              .shadow-cyber-modal {
                box-shadow: 0 0 30px rgba(0,255,255,.2), 0 0 60px rgba(138,43,226,.1), 0 20px 40px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.1);
              }
            `}</style>
          </div>

          <style jsx>{`
            @keyframes fade-in { from{opacity:0} to{opacity:1} }
            @keyframes scale-in { from{opacity:0; transform:scale(.8) translateY(40px)} to{opacity:1; transform:scale(1) translateY(0)} }
            .animate-fade-in { animation: fade-in .4s ease-out; }
            .animate-scale-in { animation: scale-in .4s ease-out; }
            .cyber-content { filter: drop-shadow(0 0 2px rgba(0,255,255,.2)); }
          `}</style>
        </div>
      )}
    </>
  );
};
// ---------------- End ShimmerCardButton ------------------------------------





/** Animated background */
const AnimatedLines = React.memo(() => {
  const [lines, setLines] = useState([]);
  useEffect(() => {
    const generateLines = () => {
      const newLines = Array.from({ length: 8 }, (_, i) => ({
        id: i,
        x1: Math.random() * 100,
        y1: Math.random() * 100,
        x2: Math.random() * 100,
        y2: Math.random() * 100,
        duration: 4 + Math.random() * 5,
        delay: Math.random() * 3,
      }));
      setLines(newLines);
    };
    generateLines();
    const interval = setInterval(generateLines, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
      <svg className="w-full h-full">
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00DDFF" stopOpacity="0" />
            <stop offset="50%" stopColor="#00DDFF" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#00DDFF" stopOpacity="0" />
          </linearGradient>
        </defs>
        {lines.map((line) => (
          <line
            key={line.id}
            x1={`${line.x1}%`}
            y1={`${line.y1}%`}
            x2={`${line.x2}%`}
            y2={`${line.y2}%`}
            stroke="url(#lineGradient)"
            strokeWidth="1"
            opacity="0"
          >
            <animate attributeName="opacity" values="0;1;0" dur={`${line.duration}s`} begin={`${line.delay}s`} repeatCount="indefinite" />
            <animateTransform
              attributeName="transform"
              type="translate"
              values={`0,0; ${(Math.random() - 0.5) * 20},${(Math.random() - 0.5) * 20}; 0,0`}
              dur={`${line.duration}s`}
              begin={`${line.delay}s`}
              repeatCount="indefinite"
            />
          </line>
        ))}
      </svg>
    </div>
  );
});

const FloatingParticles = React.memo(() => {
  const particles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    size: Math.random() * 3 + 1,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 10 + 15,
    delay: Math.random() * 5,
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute bg-[#00DDFF] rounded-full"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            left: `${p.x}%`,
            top: `${p.y}%`,
            animation: `float ${p.duration}s ease-in-out infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
});

/** -----------------------------------------------------------------------------
 *  Blog content stubs
 * --------------------------------------------------------------------------- */
const FoundersGuide = ({ setPage }) => (
  <>
    <div className="text-center mb-10">
      <h1 className="font-orbitron text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-cyan-300 via-teal-300 to-blue-400 text-transparent bg-clip-text mb-4 leading-loose">
        How I’m Building a $100K/Month Business in 90 Days with AI
      </h1>
      <div className="flex items-center justify-center space-x-6 text-sm text-gray-500 font-exo">
        <span>Published on July 25, 2025</span>
        <span className="text-gray-600">|</span>
        <span>by Achin</span>
      </div>
    </div>

    <p className="font-exo text-xl text-gray-300 leading-relaxed mb-12 text-center max-w-3xl mx-auto">
      I’m starting a public challenge today. The goal is simple and audacious: to build a business that generates $100,000 per month in revenue within the next 90 days.
    </p>

    <div className="my-12">
      <h2 className="font-orbitron text-3xl font-bold text-cyan-400 mb-6 border-l-4 border-cyan-400 pl-4">
        The Rules of the Game
      </h2>
      <ul className="space-y-4">
        {[
          'Use 100% free tools wherever possible.',
          'Rely on AI and automation for the heavy lifting.',
          'Hold zero physical inventory.',
        ].map((t) => (
          <li key={t} className="flex items-start font-exo text-lg text-gray-300">
            <svg className="w-6 h-6 mr-3 flex-shrink-0 text-cyan-400" viewBox="0 0 24 24" fill="none"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z" stroke="currentColor" strokeWidth="1.5"/><path d="M7.75 12 10.58 14.83 16.25 9.17" stroke="currentColor" strokeWidth="1.5"/></svg>
            {t}
          </li>
        ))}
      </ul>
    </div>

    <blockquote className="border-l-4 border-gray-600 pl-6 my-12 italic text-gray-400 font-exo text-lg">
      My name is Achin, and this is the story of how I’m building Syncusive. I'm documenting everything, and this is your front-row seat.
    </blockquote>

    <div className="my-12">
      <h2 className="font-orbitron text-3xl font-bold text-cyan-400 mb-4">The Problem: The Solopreneur’s Trap</h2>
      <p className="font-exo text-lg text-gray-400 leading-relaxed">
        The biggest problem that plagues founders is that we are forced to be experts at everything. The constant context-switching is a productivity killer. We collect dozens of cool tools but fail to build a cohesive system that actually works.
      </p>
    </div>

    <div className="my-12">
      <h2 className="font-orbitron text-3xl font-bold text-cyan-400 mb-4">The Solution: An AI-Powered Productivity OS</h2>
      <p className="font-exo text-lg text-gray-400 leading-relaxed">
        Syncusive isn't just another tool. It's an "Operating System"—a central hub where your tasks, projects, and content are interconnected and amplified by AI, designed to create a seamless workflow.
      </p>
    </div>

    <div className="my-12">
      <h2 className="font-orbitron text-3xl font-bold text-cyan-400 mb-4">The Tech Stack (Built for $0)</h2>
      <p className="font-exo text-lg text-gray-400 leading-relaxed">
        The entire foundation is built on free-tier services: <strong className="text-teal-300">React, Python (Flask), MongoDB Atlas, and AWS</strong>, all orchestrated by Nginx. The total cost so far? $0.
      </p>
    </div>

    <div className="mt-16 p-8 bg-gray-800/20 border border-cyan-500/20 rounded-lg text-center shadow-lg shadow-cyan-500/5">
      <h2 className="font-orbitron text-3xl font-bold text-white mb-4">Join the Journey</h2>
      <p className="font-exo text-lg text-gray-400 max-w-xl mx-auto mb-6">
        This is a public experiment in accountability and community. I'll be sharing my wins, my failures, and everything I learn along the way.
      </p>
      <a
        href="/"
        onClick={(e) => { e.preventDefault(); setPage('/'); }}
        className="inline-block bg-gradient-to-r from-cyan-400 to-blue-500 text-black font-bold font-exo px-8 py-3 rounded-lg hover:from-white hover:to-gray-200 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-cyan-400/20"
      >
        Get The "AI Productivity Matrix"
      </a>
    </div>
  </>
);

const AITool = ({ number, role, replacement, description, url }) => (
  <div className="my-10">
    <h3 className="font-orbitron text-2xl font-bold text-cyan-400 mb-4 border-l-4 border-cyan-400 pl-4">
      {number}. The {role}
    </h3>
    <div className="font-exo text-lg text-gray-400 leading-relaxed pl-6">
      <p className="mb-2">
        <strong className="text-teal-300">AI Replacement:</strong>{' '}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-teal-300 hover:text-cyan-300 underline transition-colors">
            {replacement}
          </a>
        ) : (
          replacement
        )}
      </p>
      <p>{description}</p>
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 font-semibold mt-3 inline-block hover:underline">Go to Tool &rarr;</a>
      )}
    </div>
  </div>
);

const Post2 = ({ setPage }) => (
  <>
    <div className="text-center mb-10">
      <h1 className="font-orbitron text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-cyan-300 via-teal-300 to-blue-400 text-transparent bg-clip-text mb-4 leading-loose">
        10 AI Tools That Replaced My (Imaginary) Employees
      </h1>
      <div className="flex items-center justify-center space-x-6 text-sm text-gray-500 font-exo">
        <span>Published on July 26, 2025</span>
        <span className="text-gray-600">|</span>
        <span>by Achin</span>
      </div>
    </div>
    <blockquote className="border-l-4 border-gray-600 pl-6 my-12 italic text-gray-400 font-exo text-lg">
      Let's be clear: I didn't fire anyone. As a solopreneur, I never had any employees to begin with…
    </blockquote>
    <p className="font-exo text-lg text-gray-300 leading-relaxed mb-12">
      These tools don't just assist me; they perform the core functions of a traditional startup team…
    </p>
    <AITool number={1} role="Copywriter" replacement="Jasper (with Brand Voice)" url="https://www.jasper.ai/" description="Every business needs compelling copy. I trained Jasper on my unique brand voice, and now I can generate high-converting copy that sounds authentically like me in seconds. It's like having a world-class copywriter on call 24/7." />
    <AITool number={2} role="Social Media Manager" replacement="Buffer" url="https://buffer.com/" description="My AI social media manager, Buffer, handles it all. I write one core piece of content, and its AI automatically repurposes it with the right tone and format for X, Instagram, and LinkedIn, then schedules it for the week." />
    <AITool number={3} role="Graphic Designer" replacement="Midjourney & Canva" url="https://www.midjourney.com/" description="Need a unique blog header? I use Midjourney to generate stunning, royalty-free images from a text prompt, then use Canva's 'Magic Studio' to instantly turn them into perfectly sized graphics for any platform." />
    <AITool number={4} role="Video Editor" replacement="Opus Clip" url="https://www.opus.pro/" description="My AI video editor, Opus Clip, is a game-changer. I upload one long-form video, and it automatically cuts it into viral-style short clips, adds captions, and reframes them for vertical viewing." />
    <AITool number={5} role="SEO Specialist" replacement="Surfer SEO" url="https://surferseo.com/" description="I use Surfer SEO to get a complete blueprint for a top-ranking article. I give it a target keyword, and it tells me the exact keywords to use, the ideal word count, and the structure. It takes the guesswork out of SEO." />
    <AITool number={6} role="Meeting Assistant" replacement="Fireflies.ai" url="https://fireflies.ai/" description="My AI assistant, Fireflies, joins every meeting. It transcribes the entire conversation, creates a one-click summary, and identifies all the action items. I can stay fully present, knowing my AI has it covered." />
    <AITool number={7} role="Data Analyst" replacement="Julius AI" url="https://julius.ai/" description="Instead of hiring an analyst, I just ask Julius AI questions in plain English. I can upload a spreadsheet and ask, 'What were my most profitable customer segments last quarter?' and it will give me a direct answer with a chart." />
    <AITool number={8} role="Process Documenter" replacement="Scribe" url="https://scribehow.com/" description="My AI process documenter, Scribe, automatically creates step-by-step guides. I just turn on the recorder, perform a task, and it instantly generates a beautiful guide with annotated screenshots." />
    <AITool number={9} role="Research Assistant" replacement="Perplexity AI" url="https://www.perplexity.ai/" description="Instead of sifting through Google results, I ask my AI research assistant, Perplexity, direct questions. It gives me concise, accurate answers with sources, cutting my research time by 90%." />
    <AITool number={10} role="Calendar Manager" replacement="Motion" url="https://www.usemotion.com/" description="My AI calendar manager, Motion, is the brain of my daily operations. I dump my to-do list into it, and the AI automatically schedules each task into the optimal time slot, ensuring I'm always working on what matters." />

    <div className="mt-16 p-8 bg-gray-800/20 border border-cyan-500/20 rounded-lg text-center shadow-lg shadow-cyan-500/5">
      <h2 className="font-orbitron text-3xl font-bold text-white mb-4">The Future is an AI-Powered Team</h2>
      <p className="font-exo text-lg text-gray-400 max-w-xl mx-auto mb-6">
        This isn't about replacing humans; it's about empowering solopreneurs…
      </p>
      <a
        href="/"
        onClick={(e) => { e.preventDefault(); setPage('/'); }}
        className="inline-block bg-gradient-to-r from-cyan-400 to-blue-500 text-black font-bold font-exo px-8 py-3 rounded-lg hover:from-white hover:to-gray-200 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-cyan-400/20"
      >
        Get The "AI Productivity Matrix"
      </a>
    </div>
  </>
);

/** Blog data */
const blogPosts = [
  { id: 'ai-productivity-guide', title: "Founder's Guide to AI Productivity", component: FoundersGuide, date: "July 25, 2025", excerpt: "The public journey of building Syncusive from scratch with AI and zero inventory.", tags: ['Founder Journal', 'Build in Public', 'AI'] },
  { id: 'ai-employees', title: "10 AI Tools That Replaced My Employees", component: Post2, date: "July 26, 2025", excerpt: "How a solopreneur can build an 'AI team' to automate core business functions.", tags: ['AI Tools', 'Productivity', 'Automation'] }
];

/** -----------------------------------------------------------------------------
 *  Branding & share
 * --------------------------------------------------------------------------- */
const Logo = () => (
  <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 flex-shrink-0">
    <img src="/logo.svg" alt="Syncusive Logo" className="w-full h-full" />
  </div>
);

const ShareIcons = {
  LinkedIn: <svg width="800" height="800" className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M18.72 4H5.37A1.37 1.37 0 004 5.25V18.63A1.37 1.37 0 005.37 20H18.72A1.37 1.37 0 0020 18.63V5.25A1.25 1.25 0 0018.72 4ZM9 17.34H6.67V10.21H9V17.34ZM7.89 9.13A1.23 1.23 0 117.91 6.67 1.24 1.24 0 017.89 9.13ZM17.34 17.34H15V13.44C15 12.51 14.67 11.87 13.84 11.87A1.4 1.4 0 0012.59 12.71V17.34H10.29V10.21H12.59V11.21A2.46 2.46 0 0114.69 10C16.2 10 17.34 11 17.34 13.13V17.34Z" fill="currentColor"/></svg>,
  Instagram: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.85 0 3.204-.012 3.584-.07 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
  IndiaHackers: <span className="font-bold text-lg leading-none">IH</span>,
  YouTube: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" className="w-5 h-5" fill="white"><path d="M24.4 9c-6.353 0-12.559.491-16.102 1.22C5.974 10.802 4.2 12.382 3.818 14.514 3.402 16.702 3 20.343 3 25c0 4.942.501 8.394 1.02 10.586.38 2.126 2.136 3.825 4.484 4.295C12.226 40.604 18.246 41.1 24.5 41.1s12.372-.496 16.092-1.219c2.289-.572 4.064-2.159 4.441-4.297.401-2.61.91-6.167 1.012-10.63 0-3.672-.408-7.23-1.016-10.574-.384-2.176-2.242-3.717-4.43-4.292-3.47-.51-9.395-1.01-16.144-1.01zM19 16.967 32.898 25 19 33.033z"/></svg>,
  X: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L2.25 2.25h6.894l4.264 5.637L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
};

const ShareButtons = ({ postUrl, postTitle }) => {
  const encodedUrl = encodeURIComponent(postUrl);
  const encodedTitle = encodeURIComponent(postTitle);
  const platforms = [
    { name: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, icon: ShareIcons.LinkedIn },
    { name: 'Instagram', url: `https://www.instagram.com/syncusive/`, icon: ShareIcons.Instagram },
    { name: 'IndiaHackers', url: 'https://indiehackers.com/syncusive', icon: ShareIcons.IndiaHackers },
    { name: 'YouTube', url: 'https://www.youtube.com/@syncusive', icon: ShareIcons.YouTube },
    { name: 'X', url: `https://x.com/intent/post?url=${encodedUrl}&text=${encodedTitle}`, icon: ShareIcons.X }
  ];
  return (
    <div className="absolute top-28 left-full ml-6 hidden lg:flex flex-col space-y-2">
      {platforms.map(p => (
        <a
          key={p.name}
          href={p.url}
          target="_blank"
          rel="noopener noreferrer"
          title={`Share on ${p.name}`}
          className="w-10 h-10 rounded-full bg-gray-800/60 border border-gray-700/50 hover:bg-cyan-500/50 text-gray-400 hover:text-white flex items-center justify-center transition-all duration-300 transform hover:scale-110 backdrop-blur-sm"
        >
          {p.icon}
        </a>
      ))}
    </div>
  );
};

/** Burst particle effect on social icon hover */
const SocialIconBurst = ({ children, url, name, color = '#00DDFF' }) => {
  const [burst, setBurst] = React.useState(false);
  const [particles, setParticles] = React.useState([]);

  const PARTICLE_COUNT = 10;

  const triggerBurst = () => {
    const pts = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
      const angle = (i / PARTICLE_COUNT) * 360;
      const dist = 30 + Math.random() * 20;
      const rad = (angle * Math.PI) / 180;
      return {
        id: i,
        x: Math.cos(rad) * dist,
        y: Math.sin(rad) * dist,
        size: 2 + Math.random() * 3,
        dur: 420 + Math.random() * 200,
      };
    });
    setParticles(pts);
    setBurst(true);
    setTimeout(() => setBurst(false), 650);
  };

  return (
    /* Outer wrapper: overflow-visible so particles can escape, fixed size for consistent alignment */
    <div
      className="relative flex-shrink-0"
      style={{ width: 40, height: 40, '--burst-color': color }}
    >
      {/* Actual glass link – overflow-hidden keeps backdrop-blur clipped to the circle */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={`Follow us on ${name}`}
        onMouseEnter={triggerBurst}
        className="group absolute inset-0 flex items-center justify-center rounded-full overflow-hidden
                   bg-white/10 backdrop-blur-md border border-white/20
                   hover:bg-white/20 hover:border-white/40 transition-all duration-300 hover:scale-110"
      >
        {/* icon */}
        <span className="relative z-10 flex items-center justify-center w-5 h-5 transition-all duration-300 group-hover:drop-shadow-[0_0_6px_var(--burst-color)]">
          {children}
        </span>

        {/* shimmer sweep – contained by overflow-hidden on the <a> */}
        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent
                        -translate-x-full group-hover:translate-x-full transition-transform duration-[600ms]" />

        {/* radial glow ring */}
        <span
          className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ boxShadow: `0 0 14px 2px ${color}55, 0 0 28px 4px ${color}22` }}
        />
      </a>

      {/* Burst particles – outside the overflow-hidden <a>, so they fly freely */}
      {burst && particles.map(p => (
        <span
          key={p.id}
          className="pointer-events-none absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            background: color,
            top: '50%',
            left: '50%',
            boxShadow: `0 0 ${p.size * 2}px ${color}`,
            animation: `social-burst-fly ${p.dur}ms ease-out forwards`,
            '--bx': `${p.x}px`,
            '--by': `${p.y}px`,
            zIndex: 50,
          }}
        />
      ))}

      <style>{`
        @keyframes social-burst-fly {
          0%   { transform: translate(-50%, -50%) translate(0, 0) scale(1); opacity: 1; }
          60%  { opacity: 0.8; }
          100% { transform: translate(-50%, -50%) translate(var(--bx), var(--by)) scale(0); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

const SocialFollowLinks = () => {
  const socialPlatforms = [
    { name: 'LinkedIn',      url: 'https://www.linkedin.com/company/syncusive',  icon: ShareIcons.LinkedIn,     color: '#0A66C2' },
    { name: 'Instagram',     url: 'https://www.instagram.com/syncusive/',         icon: ShareIcons.Instagram,    color: '#E1306C' },
    { name: 'Indie Hackers', url: 'https://indiehackers.com/syncusive',           icon: ShareIcons.IndiaHackers, color: '#00DDFF' },
    { name: 'YouTube',       url: 'https://www.youtube.com/@syncusive',           icon: ShareIcons.YouTube,      color: '#FF0000' },
    { name: 'X',             url: 'https://x.com/mrAchinStark',                  icon: ShareIcons.X,            color: '#ffffff' },
  ];
  return (
    <div className="flex items-center justify-center gap-3 mb-4">
      {socialPlatforms.map(p => (
        <SocialIconBurst key={p.name} url={p.url} name={p.name} color={p.color}>
          {p.icon}
        </SocialIconBurst>
      ))}
    </div>
  );
};

/** -----------------------------------------------------------------------------
 *  Header
 * --------------------------------------------------------------------------- */
const Header = ({ setPage, me, onLogin, onLogout, authChecking }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [userOpen,   setUserOpen]   = React.useState(false);

  // Lock body scroll while mobile menu is open
  React.useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const go = (path) => { setMobileOpen(false); setUserOpen(false); setPage(path); };

  const NAV_LINKS = [
    { href: '/blog',           label: 'Blog',           icon: '📝' },
    { href: '/roadmap',        label: 'Roadmap',        icon: '🗺'  },
    { href: '/task-optimizer', label: 'Task Optimizer', icon: '⚡' },
    { href: '/tools',          label: 'Tools',          icon: '🔧' },
    { href: '/support',        label: '☕ Support',     icon: '☕', accent: true },
  ];

  const navLinkStyle = (accent) => ({
    padding: '6px 14px', borderRadius: 9999, fontSize: 14,
    textDecoration: 'none', whiteSpace: 'nowrap', transition: 'background 0.2s',
    border: accent ? '1px solid rgba(250,204,21,0.35)' : '1px solid rgba(255,255,255,0.10)',
    background: accent ? 'rgba(250,204,21,0.08)' : 'rgba(255,255,255,0.05)',
    color: accent ? '#fde68a' : '#e5e7eb',
  });

  /* burger bars */
  const barBase = { display: 'block', height: 2, width: 22, background: 'white', borderRadius: 9999, transition: 'transform 0.28s ease, opacity 0.28s ease', transformOrigin: 'center' };
  const bar1 = mobileOpen ? { ...barBase, transform: 'translateY(7px) rotate(45deg)' } : barBase;
  const bar2 = mobileOpen ? { ...barBase, opacity: 0, transform: 'scaleX(0)' } : barBase;
  const bar3 = mobileOpen ? { ...barBase, transform: 'translateY(-7px) rotate(-45deg)' } : barBase;

  return (
    <>
      {/* CSS media queries – the only reliable way to switch desktop/mobile */}
      <style>{`
        #hdr-desktop-nav { display: flex; }
        #hdr-mobile-btn  { display: none; }
        #hdr-mobile-overlay { display: none; }
        @media (max-width: 767px) {
          #hdr-desktop-nav { display: none !important; }
          #hdr-mobile-btn  { display: flex !important; }
          #hdr-mobile-overlay { display: flex !important; }
        }
      `}</style>

      {/* ── sticky header bar ──────────────────────────────── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 1000, width: '100%',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      }}>
        <div style={{
          maxWidth: '80rem', margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px', height: 56,
        }}>

          {/* Logo */}
          <a href="/" onClick={(e) => { e.preventDefault(); go('/'); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, textDecoration: 'none' }}>
            <Logo />
            <span style={{ fontSize: 18, fontWeight: 700, color: 'white', letterSpacing: '-0.02em' }}>Syncusive</span>
          </a>

          {/* ── DESKTOP nav (hidden on mobile via CSS) ── */}
          <nav id="hdr-desktop-nav" style={{ alignItems: 'center', gap: 6 }}>
            {NAV_LINKS.map(({ href, label, accent }) => (
              <a key={href} href={href} onClick={(e) => { e.preventDefault(); go(href); }} style={navLinkStyle(accent)}>
                {label}
              </a>
            ))}
            {me ? (
              <div style={{ position: 'relative', marginLeft: 8 }}>
                <button type="button" onClick={() => setUserOpen((v) => !v)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  borderRadius: 9999, border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(0,0,0,0.7)', padding: '6px 12px',
                  fontSize: 14, color: 'white', cursor: 'pointer',
                }}>
                  <span style={{ display: 'inline-flex', height: 20, width: 20, alignItems: 'center', justifyContent: 'center', borderRadius: 9999, background: 'rgba(255,255,255,0.12)', fontSize: 10, fontWeight: 600 }}>
                    {(me?.name || me?.email || 'U').charAt(0).toUpperCase()}
                  </span>
                  <span style={{ color: '#d1d5db', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{me?.name || me?.email}</span>
                  <svg style={{ width: 16, height: 16, opacity: 0.6, transform: userOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.061l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z" clipRule="evenodd" />
                  </svg>
                </button>
                {userOpen && (
                  <div onMouseLeave={() => setUserOpen(false)} style={{
                    position: 'absolute', right: 0, marginTop: 8, width: 210,
                    borderRadius: 12, background: 'rgba(17,17,17,0.97)',
                    border: '1px solid rgba(255,255,255,0.10)',
                    boxShadow: '0 20px 48px -12px rgba(0,0,0,0.9)',
                    backdropFilter: 'blur(24px)', zIndex: 60,
                  }}>
                    <div style={{ padding: '10px 14px', fontSize: 12, color: '#9ca3af', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      Signed in as
                      <div style={{ color: '#e5e7eb', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{me.email || me.name}</div>
                    </div>
                    <button type="button" onClick={() => { setUserOpen(false); onLogout(); }} style={{
                      width: '100%', textAlign: 'left', padding: '10px 16px',
                      borderRadius: '0 0 12px 12px', color: '#fca5a5',
                      background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14,
                    }}>Sign out</button>
                  </div>
                )}
              </div>
            ) : (
              <button type="button" onClick={onLogin} style={{
                marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
                borderRadius: 9999, padding: '7px 18px', fontSize: 14, fontWeight: 600,
                color: 'black', background: 'white', border: 'none', cursor: 'pointer',
              }}>
                Sign in
                {authChecking
                  ? <span style={{ display: 'inline-block', height: 8, width: 8, borderRadius: 9999, background: 'rgba(0,0,0,0.3)' }} />
                  : <svg style={{ width: 14, height: 14, opacity: 0.6 }} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L10.939 10 7.23 6.29a.75.75 0 111.06-1.06l4.24 4.24a.75.75 0 010 1.06l-4.24 4.24a.75.75 0 01-1.08-.02z" clipRule="evenodd" /></svg>
                }
              </button>
            )}
          </nav>

          {/* ── MOBILE burger (shown on mobile via CSS) ── */}
          <div id="hdr-mobile-btn" style={{ alignItems: 'center', gap: 10 }}>
            {me && (
              <span style={{ display: 'inline-flex', height: 30, width: 30, alignItems: 'center', justifyContent: 'center', borderRadius: 9999, background: 'rgba(255,255,255,0.12)', fontSize: 13, fontWeight: 600, color: 'white' }}>
                {(me?.name || me?.email || 'U').charAt(0).toUpperCase()}
              </span>
            )}
            <button type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 5, width: 44, height: 44, borderRadius: 8, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
              <span style={bar1} />
              <span style={bar2} />
              <span style={bar3} />
            </button>
          </div>

        </div>
      </header>

      {/* ── MOBILE full-screen overlay (shown only when open, mobile only) ── */}
      {mobileOpen && (
        <div id="hdr-mobile-overlay" style={{
          position: 'fixed', inset: 0, zIndex: 999,
          background: 'rgba(4,6,16,0.97)',
          backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
          flexDirection: 'column',
          padding: '0 0 40px',
          overflowY: 'auto',
        }}>
          {/* top bar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', height: 56, borderBottom: '1px solid rgba(0,221,255,0.12)', flexShrink: 0 }}>
            <a href="/" onClick={(e) => { e.preventDefault(); go('/'); }} style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
              <Logo />
              <span style={{ fontSize: 18, fontWeight: 700, color: 'white', letterSpacing: '-0.02em' }}>Syncusive</span>
            </a>
            <button type="button" onClick={() => setMobileOpen(false)}
              style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#9ca3af', cursor: 'pointer', fontSize: 20, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
              ✕
            </button>
          </div>

          {/* nav links */}
          <nav style={{ flex: 1, padding: '20px 16px 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {NAV_LINKS.map(({ href, label, icon, accent }) => (
              <a key={href} href={href}
                onClick={(e) => { e.preventDefault(); go(href); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  padding: '16px 18px', borderRadius: 12,
                  fontSize: 17, fontWeight: 600, textDecoration: 'none',
                  color: accent ? '#fde68a' : '#e5e7eb',
                  background: accent ? 'rgba(250,204,21,0.05)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${accent ? 'rgba(250,204,21,0.15)' : 'rgba(255,255,255,0.06)'}`,
                  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
                }}>
                <span style={{ fontSize: 22, width: 28, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
                <span style={{ flex: 1 }}>{label}</span>
                <svg style={{ width: 16, height: 16, opacity: 0.3, flexShrink: 0 }} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L10.939 10 7.23 6.29a.75.75 0 111.06-1.06l4.24 4.24a.75.75 0 010 1.06l-4.24 4.24a.75.75 0 01-1.08-.02z" clipRule="evenodd" />
                </svg>
              </a>
            ))}
          </nav>

          {/* auth footer */}
          <div style={{ padding: '20px 16px 0', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 20 }}>
            {me ? (
              <>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 10, paddingLeft: 4 }}>
                  Signed in as <span style={{ color: '#d1d5db' }}>{me.email || me.name}</span>
                </p>
                <button type="button" onClick={() => { setMobileOpen(false); onLogout(); }}
                  style={{ width: '100%', padding: '15px 18px', borderRadius: 12, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', fontSize: 16, fontWeight: 600, cursor: 'pointer', textAlign: 'left', WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
                  🔓 Sign out
                </button>
              </>
            ) : (
              <button type="button" onClick={() => { setMobileOpen(false); onLogin(); }}
                style={{ width: '100%', padding: '16px 18px', borderRadius: 12, background: 'white', color: 'black', fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
                <svg viewBox="0 0 24 24" style={{ width: 18, height: 18 }} fill="currentColor"><path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/></svg>
                Sign in with Google
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

/** -----------------------------------------------------------------------------
 *  Footer
 * --------------------------------------------------------------------------- */
const Footer = () => (
  <footer className="relative overflow-hidden" style={{ zIndex: -100000 }}>
    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-900/80 to-slate-800/70" />
    <div className="absolute inset-0 opacity-30">
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/20 via-purple-600/20 to-pink-600/20 animate-pulse" />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/10 to-transparent" style={{ animation: 'moveGradient1 8s ease-in-out infinite' }} />
    </div>
    <div className="absolute inset-0 opacity-20">
      <div className="absolute inset-0 bg-gradient-to-l from-transparent via-purple-500/15 to-transparent" style={{ animation: 'moveGradient2 12s ease-in-out infinite reverse' }} />
      <div className="absolute inset-0 bg-gradient-to-r from-pink-500/[0.08] via-transparent to-cyan-500/[0.08]" style={{ animation: 'moveGradient3 10s ease-in-out infinite' }} />
    </div>
    <div className="absolute inset-0 opacity-15">
      {[...Array(25)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-cyan-300 rounded-full animate-pulse"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 3}s`,
            animationDuration: `${2 + Math.random() * 3}s`
          }}
        />
      ))}
    </div>

    <style>{`
      @keyframes moveGradient1 { 0%,100%{transform:translateX(-100%)} 50%{transform:translateX(100%)} }
      @keyframes moveGradient2 { 0%,100%{transform:translateX(100%) translateY(-50%)} 50%{transform:translateX(-100%) translateY(50%)} }
      @keyframes moveGradient3 { 0%,100%{transform:translateY(-100%) rotate(0)} 50%{transform:translateY(100%) rotate(180deg)} }
      @keyframes bottomGlow { 0%{background:linear-gradient(to right,transparent,#06b6d4,transparent); transform:scaleX(0.5)}
                              50%{background:linear-gradient(to right,transparent,#a855f7,transparent); transform:scaleX(1.2)}
                             100%{background:linear-gradient(to right,transparent,#ec4899,transparent); transform:scaleX(0.8)} }
      @keyframes globalGridDrift { 0%{transform:translate(0,0)} 100%{transform:translate(40px,40px)} }
    `}</style>

    <div className="relative backdrop-blur-xl bg-black/20 border-t border-white/10">
      <div className="max-w-4xl mx-auto text-center p-6 sm:p-8">
        <div className="mb-6">
          <p className="text-white/90 mb-3 text-sm sm:text-base">Follow us on:</p>
          <SocialFollowLinks />
        </div>
        <div className="space-y-4">
          <p className="text-white/90 font-medium flex items-center justify-center gap-2 text-sm sm:text-base">
            Built with <span className="inline-block animate-pulse text-red-400">❤️</span> by the
            <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent font-semibold"> Syncusive Team</span>
          </p>
          <p className="text-white/80 text-xs sm:text-sm">
            Have questions? Reach out at{' '}
            <a href="mailto:support@syncusive.com" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 decoration-cyan-400/50 hover:decoration-cyan-300 transition-all duration-300">
              support@syncusive.com
            </a>
          </p>
        </div>
        <div className="mt-6 pt-4 border-t border-white/10">
          <div className="flex items-center justify-center gap-2 text-white/60 text-xs">
            <div className="w-1.5 h-1.5 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full animate-pulse" />
            <span>&copy; {new Date().getFullYear()} Syncusive. All rights reserved.</span>
            <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-500 to-cyan-400 rounded-full animate-pulse" />
          </div>
        </div>
      </div>
    </div>

    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-2 opacity-40">
      <div className="w-full h-full bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent blur-sm" style={{ animation: 'bottomGlow 6s ease-in-out infinite alternate' }} />
    </div>
  </footer>
);

/** -----------------------------------------------------------------------------
 *  Landing, Blog index, Blog post
 * --------------------------------------------------------------------------- */
const LandingPage = ({ setPage }) => {
  useEffect(() => {
    document.title = 'Syncusive | AI-Powered Productivity Suite for Founders';
    setMetaDescription('Syncusive is an AI-powered productivity suite for solopreneurs. Automate your workflow, generate content, and scale your business faster.');
  }, []);

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const [heroRef,     heroVisible]     = useScrollReveal({ threshold: 0.05 });
  const [formRef,     formVisible]     = useScrollReveal({ threshold: 0.05 });
  const [statsRef,    statsVisible]    = useScrollReveal({ threshold: 0.05 });
  const [problemRef,  problemVisible]  = useScrollReveal({ threshold: 0.05 });
  const [gapRef,      gapVisible]      = useScrollReveal({ threshold: 0.05 });
  const [solutionRef, solutionVisible] = useScrollReveal({ threshold: 0.05 });
  const [howRef,      howVisible]      = useScrollReveal({ threshold: 0.05 });
  const [productsRef, productsVisible] = useScrollReveal({ threshold: 0.05 });
  const [ctaRef,      ctaVisible]      = useScrollReveal({ threshold: 0.05 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }
    setStatus('loading');
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value }),
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus('success');
        setMessage(data.message || "Thank you! You're on the list.");
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.message || `Something went wrong (${res.status}). Please try again.`);
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again later.');
    }
  };

  /* ── Static data ─────────────────────────────────────────────────── */
  const HERO_STATS = [
    { value: '500+',    label: 'Early subscribers' },
    { value: '12,000+', label: 'Tasks optimized in beta' },
    { value: '6 hrs',   label: 'Saved per week (avg.)' },
  ];
  const PAGE_STATS = [
    { value: '93%',    label: 'of solopreneurs burn out before PMF',     sub: 'Harvard Business Review' },
    { value: '$2T',    label: 'productivity software market by 2030',    sub: 'Grand View Research' },
    { value: '23 min', label: 'average deep focus lost per interruption', sub: 'UC Irvine study' },
    { value: '10×',    label: 'output increase reported with AI assistance', sub: 'Syncusive beta benchmark' },
  ];
  const PAIN_POINTS = [
    { icon: '🕐', stat: '23 min', label: 'lost per context switch',       desc: 'Every notification and tool-hop burns nearly half an hour of deep focus — before you even realise you\'ve lost it.' },
    { icon: '📋', stat: '40%',    label: 'of your week is busywork',       desc: 'Low-leverage tasks — formatting, chasing updates, manual logging — eat your highest-energy hours every single day.' },
    { icon: '🤖', stat: '0',      label: 'AI tools built for solo founders', desc: 'ChatGPT is for everyone. Notion is for teams. No product has been purpose-built for the solo founder execution loop.' },
    { icon: '💸', stat: '12+',    label: 'disconnected SaaS subscriptions', desc: 'A sprawl of tools that don\'t talk to each other, each one demanding its own mental context and login.' },
    { icon: '😩', stat: '93%',    label: 'of solopreneurs burn out',       desc: 'Not from bad ideas — from unsustainable execution pressure. The infrastructure to support solo building simply doesn\'t exist yet.' },
    { icon: '📊', stat: '<1%',    label: 'have a repeatable system',       desc: 'Most founders operate on intuition and spreadsheets. No compounding advantage, no flywheel — just friction.' },
  ];
  const GAP_ROWS = [
    { icon: '📝', tool: 'Notion',           verdict: 'Great for notes & wikis',       gap: 'Not designed for daily execution or AI-driven action — it\'s a blank canvas, not a co-pilot.' },
    { icon: '👥', tool: 'Asana / Linear',   verdict: 'Great for teams of 10–100',     gap: 'Overkill for a solo founder who pivots weekly. The overhead kills momentum.' },
    { icon: '💬', tool: 'ChatGPT',          verdict: 'Incredible language model',     gap: 'No integration into your workflow — you paste, it replies, you copy out. Context-switching overhead every time.' },
    { icon: '⚙️', tool: 'Zapier',           verdict: 'Powerful automation',           gap: 'Requires developer logic to set up. Breaks when your stack changes. Built for ops teams, not solo builders.' },
    { icon: '🧩', tool: 'Notion + Loom + Zapier + ...', verdict: '5-12 point solutions', gap: 'Each extra tool multiplies cognitive overhead instead of subtracting it. Integration debt compounds fast.' },
    { icon: '⚡', tool: 'Syncusive',        verdict: 'Purpose-built AI suite for solo founders', gap: 'Unified task intelligence, content, PDF tools, and utilities — one place, zero integration debt.', highlight: true },
  ];
  const FEATURES = [
    { icon: '🧠', color: '#22d3ee', title: 'AI Task Optimizer',  tag: '🟢 Live Beta',    tagColor: 'text-green-400 bg-green-400/10 border-green-400/20',  desc: 'Understands your full workload, auto-prioritises your backlog, and surfaces exactly what to focus on next — based on impact, deadlines, and your energy level.' },
    { icon: '📄', color: '#fb923c', title: 'PDF Studio',         tag: '🟡 Coming Soon',  tagColor: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20', desc: 'Drag in any document — contracts, research, investor decks. Get instant summaries, key clause extraction, and AI-generated action items in seconds.' },
    { icon: '✍️', color: '#a78bfa', title: 'Content Engine',     tag: '🔵 Roadmap',      tagColor: 'text-purple-400 bg-purple-400/10 border-purple-400/20', desc: 'Generate on-brand social posts, email sequences, and long-form content trained on your voice — not a generic GPT template.' },
    { icon: '📊', color: '#2dd4bf', title: 'Founder Dashboard',  tag: '🔵 Roadmap',      tagColor: 'text-teal-400 bg-teal-400/10 border-teal-400/20',       desc: 'One command center: KPIs, task queue, content calendar, AI insights — all in a single glanceable view. Stop opening 8 tabs to start your day.' },
  ];
  const HOW_STEPS = [
    { n: '01', icon: '✉️', color: '#22d3ee', title: 'Sign up free',           desc: 'Join early access and get instant access to Task Optimizer. No credit card, no commitment, no friction.' },
    { n: '02', icon: '📥', color: '#a78bfa', title: 'Drop your tasks in',     desc: 'Paste your backlog or describe your projects. The AI parses, structures, and prioritises automatically in seconds.' },
    { n: '03', icon: '🚀', color: '#2dd4bf', title: 'Execute with clarity',   desc: 'Every session starts with your AI-curated focus list. Work with purpose, not guesswork — ship more, stress less.' },
  ];
  const ROADMAP_TEASER = [
    { phase: 'Phase 1',   label: 'Task Optimizer',  status: '🟢 Live',    color: '#22d3ee', items: ['AI task parsing', 'Priority scoring', 'Focus mode'] },
    { phase: 'Phase 2',   label: 'Core Platform',   status: '🟡 Building', color: '#a78bfa', items: ['User accounts', 'Saved sessions', 'Usage analytics'] },
    { phase: 'Phase 2.5', label: 'PDF Studio',      status: '🔵 Next',    color: '#fb923c', items: ['Doc summarizer', 'Key extraction', 'AI action items'] },
  ];

  return (
    <main className="flex-1 overflow-x-hidden">

      {/* ═══════════════════════════════════════════════════════════════
          HERO
      ═══════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[90vh] flex items-center px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        {/* Ambient blobs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(0,221,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,221,255,0.03) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
            animation: 'globalGridDrift 40s linear infinite',
          }} />
          <div className="absolute -top-48 -left-48 w-[700px] h-[700px] rounded-full bg-cyan-500/5 blur-[150px] animate-pulse" />
          <div className="absolute top-1/2 -right-48 w-[500px] h-[500px] rounded-full bg-purple-500/5 blur-[120px] animate-pulse" style={{ animationDelay: '2s' }} />
          <div className="absolute -bottom-20 left-1/3 w-[400px] h-[400px] rounded-full bg-teal-500/[0.04] blur-[100px] animate-pulse" style={{ animationDelay: '4s' }} />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

            {/* ── Left: Copy ── */}
            <div
              ref={heroRef}
              className={`lg:col-span-7 text-center lg:text-left space-y-7 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
            >
              <div className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-4 py-1.5">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <NeonFlicker text="Now Building → Task Optimizer" color="#22d3ee" className="text-xs font-mono tracking-widest uppercase" />
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.08] text-white">
                <SplitRevealText
                  text="The AI Co-Pilot for Founders"
                  stagger={60}
                  delay={0}
                />{' '}
                <SplitRevealText
                  text="Who Build Alone."
                  wordClass="text-cyan-400"
                  stagger={70}
                  delay={600}
                />
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                <SplitRevealText
                  text="You're running a company solo — wearing every hat from PM to marketer to support. Syncusive is the AI-powered suite built to give you your time back and help you execute at a team-level pace."
                  stagger={18}
                  delay={900}
                  wordClass="text-gray-400"
                />
              </p>

              {/* Inline social proof stats */}
              <div className="flex flex-wrap gap-8 justify-center lg:justify-start">
                {HERO_STATS.map((s) => (
                  <div key={s.label} className="text-center lg:text-left">
                    <div className="text-2xl font-extrabold text-white">{s.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Free guide card */}
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 flex items-center gap-5 backdrop-blur-sm">
                <div className="flex-shrink-0 w-16 h-20 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-400/20 flex items-center justify-center text-3xl select-none">📘</div>
                <div>
                  <div className="text-xs font-mono text-cyan-400 uppercase tracking-widest mb-1">Free with signup</div>
                  <h3 className="font-bold text-base text-white">The AI Productivity Matrix</h3>
                  <p className="text-gray-400 text-sm mt-1">30+ elite AI tools reviewed and ranked for solo founders. Get it instantly when you subscribe.</p>
                </div>
              </div>
            </div>

            {/* ── Right: Email Form ── */}
            <div
              ref={formRef}
              className={`lg:col-span-5 transition-all duration-700 delay-300 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
            >
              <TiltCard className="relative bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-xl p-7 rounded-3xl border border-white/10 hover:border-cyan-400/30 transition-all duration-500 shadow-2xl shadow-black/40">
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />
                <div className="absolute top-0 left-0 w-5 h-5 border-l-2 border-t-2 border-cyan-400/30" />
                <div className="absolute top-0 right-0 w-5 h-5 border-r-2 border-t-2 border-cyan-400/30" />

                <h3 className="text-xl font-bold text-white text-center mb-1">Get Early Access</h3>
                <p className="text-gray-400 text-sm text-center mb-6">+ Free AI Productivity Matrix guide. No spam, ever.</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3.5 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/50 placeholder:text-gray-600 [color-scheme:dark] transition-all hover:border-white/20"
                    disabled={status === 'loading'}
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full py-3.5 bg-gradient-to-r from-cyan-500 to-cyan-400 text-black font-bold rounded-xl hover:from-white hover:to-gray-100 disabled:from-gray-700 disabled:to-gray-600 disabled:cursor-not-allowed transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-sm shadow-lg shadow-cyan-400/20"
                  >
                    {status === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Sending…
                      </span>
                    ) : 'Get Free Access & Guide →'}
                  </button>
                </form>

                {message && (
                  <div className={`mt-4 p-3 rounded-lg text-center text-sm ${status === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
                    {message}
                  </div>
                )}

                {/* Geography social proof */}
                <div className="mt-6 pt-5 border-t border-white/[0.08]">
                  <p className="text-xs text-gray-600 text-center mb-3">Subscribers from</p>
                  <div className="flex items-center justify-center gap-4 flex-wrap">
                    {/* Flag images, not emoji: Windows has no flag glyphs and shows "IN"/"US" letters instead */}
                    {[['in', 'India'], ['us', 'USA'], ['gb', 'UK'], ['ca', 'Canada']].map(([code, name]) => (
                      <span key={code} className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <img src={`https://flagcdn.com/w40/${code}.png`} alt="" width="16" height="12" loading="lazy" className="w-4 h-3 rounded-[2px] object-cover" />
                        {name}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Live product try-it nudge */}
                <button
                  onClick={() => setPage('/task-optimizer')}
                  className="mt-5 w-full py-3 rounded-xl border border-cyan-400/20 bg-cyan-400/5 text-cyan-400 text-sm font-medium hover:bg-cyan-400/10 hover:border-cyan-400/30 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  Try Task Optimizer Free
                </button>
              </TiltCard>
            </div>

          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee items={['AI-Powered', 'Solo Founders', 'Task Optimizer', 'PDF Studio', 'Context Intelligence', 'Automation', 'Scale Faster', 'Build in Public', 'Early Access', 'Free Guide']} />

      {/* ═══════════════════════════════════════════════════════════════
          STATS BAR
      ═══════════════════════════════════════════════════════════════ */}
      <section ref={statsRef} className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-5 transition-all duration-700 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {PAGE_STATS.map((s, i) => (
              <div
                key={i}
                className="relative rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm p-6 text-center overflow-hidden group hover:border-cyan-400/20 transition-all duration-300"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/0 to-transparent group-hover:via-cyan-400/40 transition-all duration-500" />
                <div className="text-3xl sm:text-4xl font-extrabold text-white mb-1.5">
                  <GlitchText text={s.value} />
                </div>
                <div className="text-sm text-gray-300 mb-2 leading-snug">{s.label}</div>
                <div className="text-xs text-gray-600 font-mono">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          THE PROBLEM
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full bg-red-500/[0.03] blur-[130px]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">

          <div ref={problemRef} className={`text-center mb-14 transition-all duration-700 ${problemVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              <NeonFlicker text="The Problem" color="#f87171" className="text-xs font-mono tracking-widest uppercase" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
              <SplitRevealText text="You're wearing" stagger={80} />{' '}<NeonFlicker text="8 hats." color="#f87171" className="font-extrabold" />
              <br className="hidden sm:block" />
              <SplitRevealText text="You only have" stagger={80} delay={300} />{' '}<NeonFlicker text="1 head." color="#ffffff" className="font-extrabold" />
            </h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
              The solo founder tax is real. While enterprise teams have specialists for every function, you're doing all of it alone — and the existing tools are making it worse, not better.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PAIN_POINTS.map((p, i) => (
              <div
                key={i}
                className="relative rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm p-6 overflow-hidden group hover:border-red-400/20 hover:bg-white/[0.05]"
                style={{
                  opacity: problemVisible ? 1 : 0,
                  transform: problemVisible ? 'translateY(0)' : 'translateY(2.5rem)',
                  transition: `opacity 0.6s ease ${i * 80}ms, transform 0.6s ease ${i * 80}ms, border-color 0.3s, background 0.3s`,
                }}
              >
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-red-400/0 to-transparent group-hover:via-red-400/40 transition-all duration-500" />
                <div className="text-3xl mb-3">{p.icon}</div>
                <div className="text-2xl font-extrabold text-red-400 mb-0.5">
                  <GlitchText text={p.stat} intensity={1.5} />
                </div>
                <div className="text-sm font-semibold text-white mb-2">
                  <SplitRevealText text={p.label} stagger={55} />
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          THE MARKET GAP
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -left-40 top-1/2 w-[500px] h-[500px] rounded-full bg-purple-500/[0.04] blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto">

          <div ref={gapRef} className={`text-center mb-14 transition-all duration-700 ${gapVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-purple-400/10 border border-purple-400/20 rounded-full px-4 py-1.5 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
              <span className="text-purple-400 text-xs font-mono tracking-widest uppercase">The Market Gap</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
              <SplitRevealText text="Every tool was built" stagger={70} />{' '}
              <br className="hidden sm:block" />
              <SplitRevealText text="for" stagger={70} delay={400} />{' '}
              <NeonFlicker text="someone else" color="#c084fc" className="font-extrabold" />.
            </h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
              The $2 trillion productivity software market is dominated by tools designed for enterprises and teams. Solo founders are an afterthought — or a stripped-down "starter" tier of software that was never meant for them.
            </p>
          </div>

          <div className={`rounded-2xl border border-white/10 overflow-hidden transition-all duration-700 delay-200 ${gapVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Header row */}
            <div className="hidden sm:grid sm:grid-cols-3 bg-white/[0.04] border-b border-white/10 px-6 py-3">
              <div className="text-xs font-mono text-gray-500 uppercase tracking-widest">Tool</div>
              <div className="text-xs font-mono text-gray-500 uppercase tracking-widest">What it does well</div>
              <div className="text-xs font-mono text-gray-500 uppercase tracking-widest">The gap for solo founders</div>
            </div>
            {GAP_ROWS.map((row, i) => (
              <div
                key={i}
                className={`sm:grid sm:grid-cols-3 flex flex-col gap-2 sm:gap-0 px-6 py-5 border-b border-white/[0.06] last:border-0 transition-all duration-300 ${row.highlight ? 'bg-cyan-400/5' : 'hover:bg-white/[0.02]'}`}
              >
                <div className="flex items-center gap-2.5">
                  <span className="text-xl">{row.icon}</span>
                  <span className={`text-sm font-bold ${row.highlight ? 'text-cyan-400' : 'text-white'}`}>{row.tool}</span>
                </div>
                <div className={`text-sm pr-4 ${row.highlight ? 'text-cyan-300' : 'text-gray-400'}`}>
                  <span className="sm:hidden text-xs text-gray-600 block mb-0.5">Does well: </span>
                  {row.verdict}
                </div>
                <div className={`text-sm ${row.highlight ? 'text-cyan-400/90 font-medium' : 'text-gray-500'}`}>
                  <span className="sm:hidden text-xs text-gray-600 block mb-0.5">Gap: </span>
                  {row.gap}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          THE SOLUTION
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute right-0 top-0 w-[500px] h-[500px] rounded-full bg-cyan-500/[0.04] blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">

          <div ref={solutionRef} className={`text-center mb-14 transition-all duration-700 ${solutionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-4 py-1.5 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-cyan-400 text-xs font-mono tracking-widest uppercase">The Solution</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
              <SplitRevealText text="One suite. Built for the way" stagger={65} />{' '}
              <br className="hidden sm:block" />
              <NeonFlicker text="you actually work." color="#22d3ee" className="font-extrabold" />
            </h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
              Syncusive is a growing suite of AI-powered tools designed from scratch for solo founders — not adapted from enterprise software or bolted together from APIs.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-7 overflow-hidden group hover:border-white/20 hover:bg-white/[0.05]"
                style={{
                  opacity: solutionVisible ? 1 : 0,
                  transform: solutionVisible ? 'translateY(0)' : 'translateY(2.5rem)',
                  transition: `opacity 0.6s ease ${i * 100}ms, transform 0.6s ease ${i * 100}ms, border-color 0.3s, background 0.3s`,
                  '--hover-color': f.color,
                }}
              >
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/0 to-transparent group-hover:via-white/20 transition-all duration-500" />
                <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-white/10 group-hover:border-white/25 transition-all duration-300" />
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                    style={{ background: `${f.color}15`, border: `1px solid ${f.color}25` }}
                  >
                    {f.icon}
                  </div>
                  <span className={`text-xs font-mono px-2.5 py-1 rounded-full border ${f.tagColor}`}>{f.tag}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          HOW IT WORKS
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">

          <div ref={howRef} className={`text-center mb-14 transition-all duration-700 ${howVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-teal-400/10 border border-teal-400/20 rounded-full px-4 py-1.5 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
              <span className="text-teal-400 text-xs font-mono tracking-widest uppercase">How It Works</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
              <SplitRevealText text="From chaos to clarity" stagger={70} />{' '}
              <br className="hidden sm:block" />
              <NeonFlicker text="in three steps." color="#2dd4bf" className="font-extrabold" />
            </h2>
          </div>

          <div className="relative">
            {/* Connecting line (desktop only) */}
            <div className="hidden lg:block absolute top-11 left-[calc(16.67%+1.5rem)] right-[calc(16.67%+1.5rem)] h-[1px] bg-gradient-to-r from-cyan-400/20 via-purple-400/20 to-teal-400/20" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {HOW_STEPS.map((step, i) => (
                <div
                  key={i}
                  className="text-center"
                  style={{
                    opacity: howVisible ? 1 : 0,
                    transform: howVisible ? 'translateY(0)' : 'translateY(2.5rem)',
                    transition: `opacity 0.7s ease ${i * 150}ms, transform 0.7s ease ${i * 150}ms`,
                  }}
                >
                  <div
                    className="w-24 h-24 rounded-full border-2 mx-auto mb-5 flex items-center justify-center relative text-4xl"
                    style={{ borderColor: `${step.color}40`, background: `${step.color}0e`, boxShadow: `0 0 30px ${step.color}20` }}
                  >
                    {step.icon}
                    <span
                      className="absolute -top-2 -right-2 text-xs font-mono font-bold px-2 py-0.5 rounded-full bg-[#0c0c0f] border"
                      style={{ color: step.color, borderColor: `${step.color}40` }}
                    >{step.n}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          LIVE PRODUCT CTA CARD
      ═══════════════════════════════════════════════════════════════ */}
      <section ref={productsRef} className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div
            className={`relative rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-cyan-500/5 to-purple-500/5 backdrop-blur-sm p-8 sm:p-12 overflow-hidden text-center transition-all duration-700 ${productsVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
          >
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />
            <div className="absolute top-0 left-0 w-7 h-7 border-l-2 border-t-2 border-cyan-400/30" />
            <div className="absolute top-0 right-0 w-7 h-7 border-r-2 border-t-2 border-cyan-400/30" />
            <div className="absolute bottom-0 left-0 w-7 h-7 border-l-2 border-b-2 border-purple-400/30" />
            <div className="absolute bottom-0 right-0 w-7 h-7 border-r-2 border-b-2 border-purple-400/30" />

            <div className="inline-flex items-center gap-2 bg-green-400/10 border border-green-400/20 rounded-full px-4 py-1.5 mb-5">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-green-400 text-xs font-mono tracking-widest uppercase">Live Now — Free Beta</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">
              <GlitchText text="Try Task Optimizer Today" />
            </h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto mb-8">
              AI-powered task prioritisation for solo founders. Paste your backlog, get your focus list. No account required to try it right now.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setPage('/task-optimizer')}
                className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-cyan-400 text-black font-bold rounded-xl hover:from-white hover:to-gray-100 transition-all duration-300 hover:scale-[1.02] text-sm shadow-lg shadow-cyan-400/20"
              >
                Launch Task Optimizer →
              </button>
              <button
                onClick={() => setPage('/roadmap')}
                className="px-8 py-4 border border-white/15 bg-white/5 text-white font-medium rounded-xl hover:bg-white/10 hover:border-white/25 transition-all duration-300 text-sm"
              >
                View Full Roadmap
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-6">Task Optimizer is free during beta. More tools ship throughout 2025–26.</p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          ROADMAP TEASER
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-1">What's Coming</h2>
              <p className="text-gray-500 text-sm">Building in public — here's what's next on the roadmap.</p>
            </div>
            <button
              onClick={() => setPage('/roadmap')}
              className="text-cyan-400 hover:text-white transition-colors text-sm font-mono inline-flex items-center gap-1.5 group"
            >
              <span>See all 6 phases</span>
              <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {ROADMAP_TEASER.map((p, i) => (
              <div
                key={i}
                className="relative rounded-2xl border bg-black/30 backdrop-blur-sm p-5 overflow-hidden hover:bg-black/50 transition-all duration-300"
                style={{ borderColor: `${p.color}25` }}
              >
                <div className="absolute top-0 left-0 right-0 h-[1px]" style={{ background: `linear-gradient(to right, transparent, ${p.color}40, transparent)` }} />
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-mono" style={{ color: p.color }}>{p.phase}</span>
                  <span className="text-xs text-gray-500">{p.status}</span>
                </div>
                <h3 className="text-base font-bold text-white mb-3">{p.label}</h3>
                <ul className="space-y-1.5">
                  {p.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-xs text-gray-400">
                      <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: p.color }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          FINAL CTA
      ═══════════════════════════════════════════════════════════════ */}
      <section ref={ctaRef} className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/[0.03] to-transparent" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <div className={`transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-5xl mb-5">🚀</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
              <SplitRevealText text="Ready to build" stagger={90} />{' '}<NeonFlicker text="smarter?" color="#22d3ee" className="font-extrabold" />
            </h2>
            <p className="text-gray-400 text-base sm:text-lg mb-8 max-w-lg mx-auto">
              Join 500+ solo founders getting early access to Syncusive. Get the free AI Productivity Matrix guide the moment you sign up.
            </p>
            <TiltCard className="relative bg-white/[0.04] border border-white/10 rounded-2xl p-7 backdrop-blur-sm">
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3.5 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/50 placeholder:text-gray-600 [color-scheme:dark] transition-all hover:border-white/20"
                  disabled={status === 'loading'}
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-7 py-3.5 bg-gradient-to-r from-cyan-500 to-cyan-400 text-black font-bold rounded-xl hover:from-white hover:to-gray-100 disabled:from-gray-700 disabled:to-gray-600 disabled:cursor-not-allowed transition-all duration-300 hover:scale-[1.02] text-sm shadow-lg shadow-cyan-400/20 whitespace-nowrap"
                >
                  {status === 'loading' ? '…' : 'Join the List →'}
                </button>
              </form>
              {message && (
                <div className={`mt-4 p-3 rounded-lg text-center text-sm ${status === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
                  {message}
                </div>
              )}
              <p className="text-xs text-gray-600 mt-4">No spam, ever. Unsubscribe at any time.</p>
            </TiltCard>
          </div>
        </div>
      </section>

      <Marquee items={['AI-Powered', 'Solo Founders', 'Task Optimizer', 'PDF Studio', 'Context Intelligence', 'Automation', 'Scale Faster', 'Build in Public', 'Early Access', 'Free Guide']} />

    </main>
  );
};

const BlogIndexPage = ({ setPage }) => {
  useEffect(() => {
    document.title = 'The Syncusive Blog | Syncusive';
    setMetaDescription('Browse the latest articles on AI, productivity, and building a business from Syncusive.');
  }, []);

  const [titleRef, titleVisible] = useScrollReveal({ threshold: 0.1 });

  return (
    <main className="flex-1 p-4 sm:p-6 lg:p-8 relative">
      {/* Ambient blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0,221,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,221,255,0.03) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
          animation: 'globalGridDrift 40s linear infinite',
        }} />
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-cyan-500/5 blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-purple-500/5 blur-[100px] animate-pulse" style={{animationDelay:'2s'}} />
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto">
        {/* Animated header */}
        <div ref={titleRef} className={`text-center mb-14 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-4 py-1.5 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-cyan-400 text-xs font-mono tracking-widest uppercase">Live Feed</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 relative inline-block">
            The Syncusive Blog
            <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-pulse" />
          </h1>
          <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto mt-4">
            Dispatches from the frontier of AI-powered solopreneurship.
          </p>
        </div>

        {/* Cards */}
        <div className="space-y-8">
          {blogPosts.map((post, i) => (
            <BlogCard key={post.id} post={post} index={i} setPage={setPage} />
          ))}
        </div>

        {/* Bottom marquee */}
        <Marquee items={['AI Strategy', 'Founder Journal', 'Productivity', 'Build in Public', 'Automation', 'AI Tools']} />
      </div>
    </main>
  );
};

/** Animated individual blog card */
const BlogCard = ({ post, index, setPage }) => {
  const [ref, visible] = useScrollReveal({ threshold: 0.08 });
  const [hovered, setHovered] = React.useState(false);
  const [mousePos, setMousePos] = React.useState({ x: 0, y: 0 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setMousePos({ x: e.clientX - r.left, y: e.clientY - r.top });
  };

  const tagColors = ['text-cyan-400 bg-cyan-400/10 border-cyan-400/20', 'text-purple-400 bg-purple-400/10 border-purple-400/20', 'text-teal-400 bg-teal-400/10 border-teal-400/20'];
  const tags = post.tags || ['AI', 'Productivity'];

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <div
        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm cursor-pointer"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onMouseMove={onMouseMove}
        onClick={() => setPage(`/blog/${post.id}`)}
      >
        {/* Torch spotlight that follows cursor */}
        {hovered && (
          <div
            className="pointer-events-none absolute inset-0 z-0 transition-opacity duration-300"
            style={{
              background: `radial-gradient(300px circle at ${mousePos.x}px ${mousePos.y}px, rgba(0,221,255,0.07), transparent 70%)`,
            }}
          />
        )}

        {/* Top glowing border on hover */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/0 to-transparent group-hover:via-cyan-400/70 transition-all duration-500" />

        {/* Animated corner accents */}
        <div className="absolute top-0 left-0 w-5 h-5 border-l-2 border-t-2 border-cyan-400/0 group-hover:border-cyan-400/60 transition-all duration-500" />
        <div className="absolute top-0 right-0 w-5 h-5 border-r-2 border-t-2 border-cyan-400/0 group-hover:border-cyan-400/60 transition-all duration-500" />
        <div className="absolute bottom-0 left-0 w-5 h-5 border-l-2 border-b-2 border-purple-400/0 group-hover:border-purple-400/60 transition-all duration-500" />
        <div className="absolute bottom-0 right-0 w-5 h-5 border-r-2 border-b-2 border-purple-400/0 group-hover:border-purple-400/60 transition-all duration-500" />

        {/* Live dot */}
        <div className="absolute top-4 right-4 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          <span className="text-[10px] text-green-400/70 font-mono uppercase tracking-wider">Live</span>
        </div>

        <div className="relative z-10 p-6 sm:p-8">
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {tags.map((tag, ti) => (
              <span key={tag} className={`text-xs font-mono px-2.5 py-0.5 rounded-full border ${tagColors[ti % tagColors.length]}`}>
                {tag}
              </span>
            ))}
          </div>

          <p className="text-xs text-gray-500 font-mono mb-2 tracking-wide">{post.date}</p>

          <h2 className="text-2xl sm:text-3xl font-bold text-white group-hover:text-cyan-300 transition-colors duration-300 mb-3 leading-snug">
            {post.title}
          </h2>

          <p className="text-gray-400 group-hover:text-gray-300 transition-colors duration-300 leading-relaxed mb-6">
            {post.excerpt}
          </p>

          {/* Animated read-more button */}
          <div className="inline-flex items-center gap-2 text-cyan-400 font-semibold text-sm group/btn">
            <span className="group-hover/btn:text-white transition-colors duration-200">Read Article</span>
            <span className="inline-block transform translate-x-0 group-hover/btn:translate-x-2 transition-transform duration-300">&rarr;</span>
            <span className="h-[1px] w-0 bg-cyan-400 group-hover/btn:w-12 transition-all duration-300" />
          </div>
        </div>

        {/* Bottom shimmer bar */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-cyan-500/0 via-cyan-500/60 to-purple-500/60 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-700" />
      </div>
    </div>
  );
};

const BlogPostPage = ({ postId, setPage }) => {
  const post = blogPosts.find(p => p.id === postId);
  const [readPct, setReadPct] = React.useState(0);
  const contentRef = useRef(null);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Syncusive`;
      setMetaDescription(post.excerpt);
    }
    setReadPct(0);
    window.scrollTo({ top: 0 });
  }, [post]);

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      setReadPct(total > 0 ? Math.min(100, (scrolled / total) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [post]);

  if (!post) {
    return (
      <main className="flex-1 p-4 sm:p-6 lg:p-8 text-center">
        <h1 className="text-4xl font-bold text-white">404 - Post Not Found</h1>
        <p className="text-gray-400 mt-4">The blog post you're looking for doesn't exist.</p>
        <a href="/blog" onClick={(e) => { e.preventDefault(); setPage('/blog'); }} className="text-cyan-400 hover:underline mt-8 inline-block">&larr; Back to all posts</a>
      </main>
    );
  }

  const PostComponent = post.component;

  return (
    <main className="flex-1 p-4 sm:p-6 lg:p-8 relative">
      {/* Per-article reading progress bar (separate from global ScrollProgressBar) */}
      <div className="fixed top-[2px] left-0 right-0 z-[9997] h-[2px] bg-transparent pointer-events-none">
        <div
          className="h-full bg-gradient-to-r from-purple-500 via-cyan-400 to-teal-400 transition-none"
          style={{ width: `${readPct}%`, boxShadow: '0 0 8px rgba(0,221,255,0.6)' }}
        />
      </div>

      {/* Reading % badge */}
      <div
        className="fixed bottom-6 right-6 z-50 w-11 h-11 rounded-full border-2 border-cyan-400/40 bg-black/80 backdrop-blur-md flex items-center justify-center text-[10px] font-mono text-cyan-400 shadow-lg shadow-cyan-400/10 pointer-events-none"
        style={{ background: `conic-gradient(rgba(0,221,255,0.6) ${readPct * 3.6}deg, rgba(255,255,255,0.04) 0deg)` }}
      >
        <span className="absolute inset-[3px] rounded-full bg-black/90 flex items-center justify-center">
          {Math.round(readPct)}%
        </span>
      </div>

      {/* Ambient blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0,221,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,221,255,0.03) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
          animation: 'globalGridDrift 40s linear infinite',
        }} />
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-cyan-500/[0.04] blur-[100px] animate-pulse" />
        <div className="absolute bottom-20 -left-20 w-[350px] h-[350px] rounded-full bg-purple-500/[0.04] blur-[90px] animate-pulse" style={{animationDelay:'3s'}} />
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto">
        <ShareButtons
          postUrl={typeof window !== 'undefined' ? window.location.href : ''}
          postTitle={post.title}
        />

        {/* Back nav */}
        <a
          href="/blog"
          onClick={(e) => { e.preventDefault(); setPage('/blog'); }}
          className="inline-flex items-center gap-2 text-cyan-400 hover:text-white text-sm font-mono mb-8 group transition-colors duration-200"
        >
          <span className="transform group-hover:-translate-x-1 transition-transform duration-200">&larr;</span>
          <span>Back to all posts</span>
        </a>

        {/* Article card */}
        <div className="w-full bg-[#0e0e0e]/80 border border-white/[0.08] rounded-2xl p-6 sm:p-10 backdrop-blur-xl relative overflow-hidden">
          {/* Top glow line */}
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />
          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 border-cyan-400/30" />
          <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-cyan-400/30" />
          <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-purple-400/30" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 border-purple-400/30" />

          <PostComponent setPage={setPage} />
        </div>
      </div>
    </main>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ROADMAP PAGE
// ─────────────────────────────────────────────────────────────────────────────
const ROADMAP = [
  {
    phase: 'Phase 1', label: 'Foundation', status: 'done',
    period: 'Q3 2025',
    color: 'cyan',
    items: [
      'Core landing page & waitlist',
      'Task Optimizer v1 (AI scheduling)',
      'Google OAuth login',
      'Founder\'s Blog — public build log',
      'Email onboarding sequence',
    ],
  },
  {
    phase: 'Phase 2', label: 'AI Engine', status: 'active',
    period: 'Q1 2026',
    color: 'purple',
    items: [
      'AI Content Pipeline planner',
      'Daily stand-up automation',
      'Custom GPT brain for founders',
      'Task Optimizer v2 — calendar sync',
      'Mobile-responsive PWA',
    ],
  },
  {
    phase: 'Phase 2.5', label: 'PDF Studio — Side Project', status: 'active',
    period: 'Q1–Q2 2026',
    color: 'orange',
    items: [
      'Browser-based PDF editor (text edit & delete)',
      'Image insertion & element replacement',
      'Page resizer & margin controls',
      'Affordable alternative to PDFGuru',
      'Keeps the AI mission funded & self-sustaining',
    ],
  },
  {
    phase: 'Phase 3', label: 'Monetisation', status: 'upcoming',
    period: 'Q2 2026',
    color: 'teal',
    items: [
      'Pro subscription tier',
      'Team workspaces',
      'API access for power users',
      'Affiliate & referral program',
      'Marketplace of AI prompt packs',
    ],
  },
  {
    phase: 'Phase 4', label: 'Scale', status: 'upcoming',
    period: 'Q3–Q4 2026',
    color: 'yellow',
    items: [
      'Dedicated AI model (fine-tuned for founders)',
      'Integrations: Notion, Linear, Slack',
      'Analytics dashboard',
      'Community forum',
      'Mobile app (iOS & Android)',
    ],
  },
  {
    phase: 'Phase 5', label: 'Daily Utility Suite', status: 'upcoming',
    period: '2027',
    color: 'pink',
    items: [
      'Image format converter (PNG, WEBP, JPEG & more)',
      'Audio editor — trim, merge, convert MP3',
      'Video tools — cut, compress, convert MP4',
      'All-in-one free utility hub for everyday needs',
      'Cross-sell funnel back to Syncusive AI suite',
    ],
  },
];

const statusMeta = {
  done:     { label: 'Completed', dot: 'bg-green-400',  ring: 'border-green-400/40',  text: 'text-green-400'  },
  active:   { label: 'In Progress', dot: 'bg-cyan-400 animate-pulse', ring: 'border-cyan-400/60', text: 'text-cyan-400' },
  upcoming: { label: 'Upcoming',  dot: 'bg-gray-500',   ring: 'border-gray-600/40',   text: 'text-gray-400'  },
};

const phaseColors = {
  cyan:   { border: 'border-cyan-400/30',   glow: 'shadow-cyan-400/10',   accent: 'text-cyan-400',   bar: 'from-cyan-500 to-cyan-300'   },
  purple: { border: 'border-purple-400/30', glow: 'shadow-purple-400/10', accent: 'text-purple-400', bar: 'from-purple-500 to-purple-300' },
  teal:   { border: 'border-teal-400/30',   glow: 'shadow-teal-400/10',   accent: 'text-teal-400',   bar: 'from-teal-500 to-teal-300'   },
  yellow: { border: 'border-yellow-400/30', glow: 'shadow-yellow-400/10', accent: 'text-yellow-400', bar: 'from-yellow-500 to-yellow-300' },
  orange: { border: 'border-orange-400/30', glow: 'shadow-orange-400/10', accent: 'text-orange-400', bar: 'from-orange-500 to-orange-300' },
  pink:   { border: 'border-pink-400/30',   glow: 'shadow-pink-400/10',   accent: 'text-pink-400',   bar: 'from-pink-500 to-pink-300'   },
};

const RoadmapPhaseCard = ({ phase, idx }) => {
  const [ref, visible] = useScrollReveal({ threshold: 0.08 });
  const sm = statusMeta[phase.status];
  const pc = phaseColors[phase.color];
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
      }`}
      style={{ transitionDelay: `${idx * 100}ms` }}
    >
      <div className={`relative sm:ml-16 rounded-2xl border ${pc.border} bg-white/[0.025] backdrop-blur-sm p-6 shadow-xl ${pc.glow}`}>
        {/* Spine dot */}
        <div className={`hidden sm:flex absolute -left-[2.85rem] top-6 w-5 h-5 rounded-full border-2 ${sm.ring} items-center justify-center bg-black`}>
          <span className={`w-2 h-2 rounded-full ${sm.dot}`} />
        </div>
        {/* Top glow */}
        <div className={`absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-current to-transparent ${pc.accent} opacity-40`} />
        {/* Corner accents */}
        <div className={`absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 ${pc.border}`} />
        <div className={`absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 ${pc.border}`} />
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <span className={`text-xs font-mono px-2 py-0.5 rounded border ${pc.border} ${pc.accent}`}>{phase.phase}</span>
            <h2 className={`text-xl font-bold ${pc.accent}`}>{phase.label}</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 font-mono">{phase.period}</span>
            <span className={`inline-flex items-center gap-1.5 text-xs font-mono px-2.5 py-0.5 rounded-full border ${sm.ring} ${sm.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`} />
              {sm.label}
            </span>
          </div>
        </div>
        <ul className="space-y-2">
          {phase.items.map((item, ii) => (
            <li key={ii} className="flex items-start gap-3 text-sm">
              <span className={`mt-0.5 flex-shrink-0 ${phase.status === 'done' ? 'text-green-400' : pc.accent}`}>
                {phase.status === 'done' ? '✓' : '→'}
              </span>
              <span className={phase.status === 'done' ? 'text-gray-400 line-through decoration-gray-600' : 'text-gray-300'}>
                {item}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const RoadmapPage = ({ setPage }) => {
  useEffect(() => {
    document.title = 'Roadmap | Syncusive';
    setMetaDescription('See what Syncusive is building next — our public product roadmap for AI-powered founder tools.');
    window.scrollTo({ top: 0 });
  }, []);

  const [titleRef, titleVisible] = useScrollReveal({ threshold: 0.05 });

  return (
    <main className="flex-1 p-4 sm:p-6 lg:p-8 relative">
      {/* Ambient blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0,221,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,221,255,0.03) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
          animation: 'globalGridDrift 40s linear infinite',
        }} />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[300px] rounded-full bg-cyan-500/5 blur-[130px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-purple-500/5 blur-[110px] animate-pulse" style={{animationDelay:'2s'}} />
      </div>


      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div ref={titleRef} className={`text-center mb-16 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 bg-purple-400/10 border border-purple-400/20 rounded-full px-4 py-1.5 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
            <span className="text-purple-400 text-xs font-mono tracking-widest uppercase">Public Roadmap</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 relative inline-block">
            What We're Building
            <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-purple-400 to-transparent" />
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto mt-5 text-base sm:text-lg">
            Syncusive is a publicly-built AI productivity suite. Here's exactly where we are and where we're headed.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical spine */}
          <div className="absolute left-6 top-0 bottom-0 w-[2px] bg-gradient-to-b from-cyan-400/40 via-purple-400/30 to-transparent hidden sm:block" />

          <div className="space-y-10">
            {ROADMAP.map((phase, idx) => (
              <RoadmapPhaseCard key={phase.phase} phase={phase} idx={idx} />
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 mb-4">Want to speed this up? Help us get the hardware we need.</p>
          <button
            onClick={() => setPage('/support')}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-400 text-black font-bold px-8 py-3 rounded-xl hover:from-yellow-400 hover:to-yellow-300 transition-all duration-300 shadow-lg shadow-yellow-400/20 hover:scale-105"
          >
            ☕ Support the Mission
          </button>
        </div>
      </div>
    </main>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUPPORT / DONATION PAGE  (PayPal)
// ─────────────────────────────────────────────────────────────────────────────
const PAYPAL_ME = 'https://paypal.me/AchinStark'; // ← replace with your PayPal.me link
const TIERS = [
  { emoji: '☕', label: 'Buy a Coffee', amount: 5,   desc: 'Keep the founder caffeinated.' },
  { emoji: '🔧', label: 'GPU Hour',     amount: 25,  desc: 'Pay for compute time to train our AI.' },
  { emoji: '💻', label: 'PC Fund',      amount: 100, desc: 'Directly toward the new dev machine.' },
  { emoji: '🚀', label: 'Rocket Fuel',  amount: 250, desc: 'Full sprint of AI development.' },
];

const SupportPage = ({ setPage }) => {
  const [custom, setCustom] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [sent, setSent] = React.useState(false);
  const [titleRef, titleVisible] = useScrollReveal({ threshold: 0.05 });

  // ── Auto-running roadmap ticker ─────────────────────────────────────────
  const [tickerPhaseIdx, setTickerPhaseIdx] = React.useState(0);
  // -1 = phase header, 0..n-1 = item, n = CTA
  const [tickerItemIdx, setTickerItemIdx] = React.useState(-1);
  const [tickerIn, setTickerIn] = React.useState(true);

  useEffect(() => {
    const phase = ROADMAP[tickerPhaseIdx];
    const delay = tickerItemIdx === -1 ? 1800
                : tickerItemIdx < phase.items.length ? 1500
                : 2400;
    const t = setTimeout(() => {
      setTickerIn(false);
      setTimeout(() => {
        setTickerItemIdx(cur => {
          if (cur < phase.items.length) return cur + 1;
          setTickerPhaseIdx(p => (p + 1) % ROADMAP.length);
          return -1;
        });
        setTickerIn(true);
      }, 300);
    }, delay);
    return () => clearTimeout(t);
  }, [tickerPhaseIdx, tickerItemIdx]);

  const tPhase   = ROADMAP[tickerPhaseIdx];
  const tPc      = phaseColors[tPhase.color];
  const isHeader = tickerItemIdx === -1;
  const isCTA    = tickerItemIdx >= tPhase.items.length;
  // ────────────────────────────────────────────────────────────────────────

  const handleDonate = (amount) => {
    const amt = Number(amount);
    if (!amt || amt < 1) return;
    const url = `${PAYPAL_ME}/${amt}USD`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setSent(true);
  };

  useEffect(() => {
    document.title = 'Support Syncusive | Help Us Build AI';
    setMetaDescription('Support Syncusive — help a solo founder buy the hardware needed to build an AI productivity engine for founders.');
    window.scrollTo({ top: 0 });
  }, []);

  // hex colours that match ROADMAP order (cyan, purple, orange, teal, yellow, pink)
  const PHASE_HEX = ['#22d3ee','#a78bfa','#fb923c','#2dd4bf','#facc15','#f472b6'];

  return (
    <main className="flex-1 p-4 sm:p-6 lg:p-8 relative">
      {/* Ambient blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0,221,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,221,255,0.03) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
          animation: 'globalGridDrift 40s linear infinite',
        }} />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[250px] rounded-full bg-yellow-500/5 blur-[120px] animate-pulse" />
        <div className="absolute bottom-10 right-10 w-[350px] h-[350px] rounded-full bg-cyan-500/[0.04] blur-[100px] animate-pulse" style={{animationDelay:'2s'}} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Hero — full width */}
        <div ref={titleRef} className={`text-center mb-10 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/20 rounded-full px-4 py-1.5 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
            <span className="text-yellow-400 text-xs font-mono tracking-widest uppercase">Community Support</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4">Help Build the Future</h1>
          <p className="text-gray-400 max-w-2xl mx-auto mt-4 text-base sm:text-lg leading-relaxed">
            I'm a solo founder building <span className="text-cyan-400 font-semibold">Syncusive</span> — an AI productivity suite for founders.
            Your support goes <span className="text-yellow-400 font-semibold">directly toward hardware</span> that lets me ship faster.
          </p>
        </div>

        {/* ════════════════════════ Two-column body ════════════════════════ */}
        <div className="flex flex-col lg:flex-row gap-10 items-start">

          {/* ── LEFT: Why card + Live Roadmap ── */}
          <div className="w-full lg:w-1/2 lg:sticky lg:top-8">

            {/* Why I need your help */}
            <div className="relative rounded-2xl border border-yellow-400/20 bg-white/[0.025] backdrop-blur-sm p-6 mb-8 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-yellow-400/50 to-transparent" />
              <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-yellow-400/30" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-yellow-400/30" />
              <h2 className="text-lg font-bold text-yellow-400 mb-3">Why I need your help</h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Running on a shared laptop while context-switching between coding, design, and marketing.
                A dedicated GPU workstation lets me:
              </p>
              <ul className="space-y-2 mb-5">
                {[
                  'Fine-tune a local AI model (no OpenAI bill)',
                  'Run experiments 10× faster',
                  'Ship Task Optimizer v2 in weeks, not months',
                  'Keep Syncusive free for early users longer',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-300">
                    <span className="text-yellow-400 flex-shrink-0 mt-0.5">→</span>{t}
                  </li>
                ))}
              </ul>
              <div className="flex gap-3">
                <div className="flex-1 bg-white/5 rounded-xl p-3 border border-white/[0.08] text-center">
                  <div className="text-xl font-bold text-cyan-400">$0</div>
                  <div className="text-xs text-gray-500 mt-1">Raised</div>
                  <div className="mt-1.5 h-1 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full w-0 bg-gradient-to-r from-cyan-500 to-cyan-300 rounded-full" />
                  </div>
                </div>
                <div className="flex-1 bg-white/5 rounded-xl p-3 border border-white/[0.08] text-center">
                  <div className="text-xl font-bold text-yellow-400">$1,200</div>
                  <div className="text-xs text-gray-500 mt-1">Goal</div>
                </div>
              </div>
            </div>

            {/* Animated Roadmap Timeline */}
            <div className="relative">
              <p className="text-xs font-mono text-gray-600 uppercase tracking-widest mb-6 pl-10">What your support builds</p>
              {/* Static grey spine */}
              <div className="absolute left-[1.1rem] top-[2.2rem] bottom-4 w-0.5 rounded-full bg-white/[0.06]" />
              {/* Colour fill grows as ticker advances through phases */}
              <div
                className="absolute left-[1.1rem] top-[2.2rem] w-0.5 rounded-full transition-all duration-700 ease-in-out"
                style={{
                  background: `linear-gradient(to bottom, ${PHASE_HEX[tickerPhaseIdx]}, transparent)`,
                  height: `${Math.min(96,
                    (tickerPhaseIdx / ROADMAP.length) * 100
                    + (Math.max(0, tickerItemIdx) / (ROADMAP[tickerPhaseIdx].items.length * ROADMAP.length)) * 100
                  )}%`,
                  opacity: 0.8,
                }}
              />
              {ROADMAP.map((phase, pi) => {
                const isActivePhase = pi === tickerPhaseIdx;
                const isPastPhase   = pi < tickerPhaseIdx;
                const hex           = PHASE_HEX[pi];
                return (
                  <div key={phase.phase} className="relative mb-6 pl-10">
                    {/* Phase dot */}
                    <div
                      className="absolute left-0 top-0.5 w-[1.6rem] h-[1.6rem] rounded-full border-2 flex items-center justify-center bg-[#0c0c0f] transition-all duration-500"
                      style={{
                        borderColor: isActivePhase || isPastPhase ? hex : 'rgba(255,255,255,0.08)',
                        boxShadow:   isActivePhase ? `0 0 14px 3px ${hex}55` : 'none',
                      }}
                    >
                      <span
                        className={`w-2 h-2 rounded-full transition-all duration-500 ${isActivePhase ? 'animate-pulse' : ''}`}
                        style={{ background: isActivePhase || isPastPhase ? hex : 'rgba(255,255,255,0.08)' }}
                      />
                    </div>
                    {/* Phase header */}
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="text-xs font-mono font-bold" style={{ color: isActivePhase || isPastPhase ? hex : 'rgba(156,163,175,0.3)' }}>{phase.phase}</span>
                      <span className="text-sm font-bold transition-colors duration-500" style={{ color: isActivePhase ? '#fff' : isPastPhase ? 'rgba(156,163,175,0.6)' : 'rgba(75,85,99,0.6)' }}>{phase.label}</span>
                      <span className="text-xs font-mono ml-auto" style={{ color: 'rgba(75,85,99,0.7)' }}>{phase.period}</span>
                    </div>
                    {/* Items */}
                    <ul className="space-y-1.5">
                      {phase.items.map((item, ii) => {
                        const isActiveItem = isActivePhase && ii === tickerItemIdx && !isCTA;
                        const isPastItem   = isPastPhase || (isActivePhase && ii < tickerItemIdx);
                        return (
                          <li key={ii} className="flex items-start gap-2 text-xs transition-all duration-500"
                            style={{ color: isActiveItem ? '#fff' : isPastItem ? 'rgba(156,163,175,0.55)' : 'rgba(75,85,99,0.55)' }}
                          >
                            <span
                              className={`flex-shrink-0 rounded-full transition-all duration-500 ${isActiveItem ? 'animate-pulse' : ''}`}
                              style={{
                                width: '5px', height: '5px', marginTop: '5px',
                                background: isActiveItem ? hex : isPastItem ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.05)',
                                boxShadow:  isActiveItem ? `0 0 7px 2px ${hex}` : 'none',
                              }}
                            />
                            <span className={isActiveItem ? 'font-semibold' : ''}>{item}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── RIGHT: Payment panel ── */}
          <div className="w-full lg:w-1/2">

            {/* Live "currently building" badge */}
            <div
              className="relative rounded-2xl border p-4 mb-6 overflow-hidden bg-black/40 backdrop-blur-sm"
              style={{ borderColor: `${PHASE_HEX[tickerPhaseIdx]}40` }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-[1px] opacity-50"
                style={{ background: `linear-gradient(to right, transparent, ${PHASE_HEX[tickerPhaseIdx]}, transparent)` }}
              />
              {isCTA ? (
                <div className="text-center py-1">
                  <p className="text-gray-500 text-xs font-mono uppercase tracking-widest mb-1.5">All phases planned</p>
                  <span className="text-base font-bold text-yellow-400">☕ Donate now and help make it real</span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center"
                    style={{ borderColor: `${PHASE_HEX[tickerPhaseIdx]}60` }}
                  >
                    <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: PHASE_HEX[tickerPhaseIdx] }} />
                  </div>
                  <div className="overflow-hidden">
                    <div className="text-xs text-gray-600 font-mono uppercase tracking-wider mb-0.5">Currently building</div>
                    <div className="text-sm font-semibold truncate transition-all duration-300"
                      style={{ color: PHASE_HEX[tickerPhaseIdx], opacity: tickerIn ? 1 : 0, transform: tickerIn ? 'translateY(0)' : 'translateY(6px)' }}
                    >
                      {isHeader ? `${tPhase.phase}: ${tPhase.label}` : tPhase.items[tickerItemIdx]}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tiers */}
            <h2 className="text-xs font-bold text-white mb-3 font-mono uppercase tracking-widest">Choose your contribution</h2>
            <div className="grid grid-cols-2 gap-3 mb-5">
              {TIERS.map((tier) => (
                <button
                  key={tier.amount}
                  onClick={() => { setSelected(tier.amount); handleDonate(tier.amount); }}
                  className={`group relative text-left rounded-2xl border p-4 transition-all duration-300 overflow-hidden
                    ${selected === tier.amount
                      ? 'border-yellow-400/70 bg-yellow-400/10 shadow-lg shadow-yellow-400/10'
                      : 'border-white/10 bg-white/[0.02] hover:border-yellow-400/40 hover:bg-white/5'}`}
                >
                  <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-yellow-400/0 to-transparent group-hover:via-yellow-400/50 transition-all duration-500" />
                  <div className="text-2xl mb-1.5">{tier.emoji}</div>
                  <div className="flex items-baseline gap-1.5 mb-0.5">
                    <span className="text-lg font-bold text-white">${tier.amount}</span>
                    <span className="text-xs text-gray-400">{tier.label}</span>
                  </div>
                  <p className="text-xs text-gray-500">{tier.desc}</p>
                </button>
              ))}
            </div>


            {/* Custom amount */}
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-5">
              <h3 className="text-xs font-mono text-gray-500 mb-3 uppercase tracking-widest">Custom amount (USD)</h3>
              <div className="flex gap-1.5 mb-3 flex-wrap">
                {[5, 10, 15, 25, 50, 75].map(n => (
                  <button
                    key={n}
                    onClick={() => setCustom(String(Number(custom || 0) + n))}
                    className="px-2.5 py-1 text-xs font-mono rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-yellow-400/10 hover:border-yellow-400/30 hover:text-yellow-300 transition-all duration-200"
                  >+${n}</button>
                ))}
                <button
                  onClick={() => setCustom('')}
                  className="px-2.5 py-1 text-xs font-mono rounded-lg bg-white/5 border border-white/10 text-gray-500 hover:text-white transition-all duration-200 ml-auto"
                >Clear</button>
              </div>
              <div className="flex gap-2 items-center mb-3">
                <button
                  onClick={() => setCustom(v => String(Math.max(1, Number(v || 1) - 1)))}
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-white text-xl font-bold flex-shrink-0 hover:bg-white/10 transition-all duration-200"
                >−</button>
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                  <input
                    type="number" min="1" placeholder="0"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-xl text-white text-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400/50 placeholder:text-gray-600 [color-scheme:dark]"
                  />
                </div>
                <button
                  onClick={() => setCustom(v => String(Number(v || 0) + 1))}
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-white text-xl font-bold flex-shrink-0 hover:bg-white/10 transition-all duration-200"
                >+</button>
              </div>
              <button
                onClick={() => handleDonate(custom)}
                disabled={!custom || Number(custom) < 1}
                className="w-full py-3 bg-gradient-to-r from-yellow-500 to-yellow-400 text-black font-bold text-sm rounded-xl hover:from-yellow-400 hover:to-yellow-300 disabled:from-gray-700 disabled:to-gray-600 disabled:cursor-not-allowed transition-all duration-300 hover:scale-[1.01] active:scale-[0.99]"
              >
                {custom && Number(custom) >= 1 ? `Donate $${custom}` : 'Enter an amount'}
              </button>
            </div>

            {/* PayPal note */}
            <div className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 mb-5">
              <svg viewBox="0 0 24 24" className="w-7 h-7 flex-shrink-0 text-[#003087]" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082H9.827l-1.35 8.569H12.2c.459 0 .85-.334.922-.788l.038-.199.731-4.63.047-.255a.932.932 0 0 1 .922-.788h.58c3.76 0 6.705-1.528 7.563-5.95.36-1.845.173-3.386-.781-4.754z"/></svg>
              <div>
                <p className="text-white text-sm font-medium">Secure payment via PayPal</p>
                <p className="text-gray-500 text-xs mt-0.5">Opens PayPal in a new tab. No account required for card payments.</p>
              </div>
            </div>

            {sent && (
              <div className="rounded-2xl border border-green-400/30 bg-green-400/5 p-5 text-center mb-5">
                <div className="text-3xl mb-1.5">🙏</div>
                <h3 className="text-lg font-bold text-green-400 mb-1">Thank you!</h3>
                <p className="text-gray-400 text-sm">Your support means the world. I'll shout you out in the next build log.</p>
              </div>
            )}

            <div className="text-center mt-6">
              <p className="text-gray-600 text-xs mb-2">Want to see the full plan?</p>
              <button
                onClick={() => setPage('/roadmap')}
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-white transition-colors text-sm font-mono group"
              >
                <span>View the Roadmap</span>
                <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
              </button>
            </div>
          </div>

        </div>
      </div>
    </main>
  );
};

/** -----------------------------------------------------------------------------
 *  App (router + SSO)
 * --------------------------------------------------------------------------- */
export default function App() {
  const [page, setPage] = useState(window.location.pathname);
  const [me, setMe] = useState(null);
  const [authChecking, setAuthChecking] = useState(false);

 const fetchMe = useCallback(async () => {
   try {
     const res = await fetch(`${API_BASE}/api/auth/status`, { credentials: 'include' });
     if (res.ok) {
       const data = await res.json();
       setMe(data && data.authenticated ? (data.user || null) : null);
     } else {
       setMe(null);
     }
   } catch {
     setMe(null);
   }
 }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Clean any auth noise params then refetch
  useEffect(() => {
    const url = new URL(window.location.href);
    const keys = ['auth', 'logged', 'login', 'code', 'state'];
    let changed = false;
    keys.forEach(k => { if (url.searchParams.has(k)) { url.searchParams.delete(k); changed = true; } });
    if (changed) {
      window.history.replaceState({}, '', url.pathname + url.hash);
      fetchMe();
    }
  }, [fetchMe]);

  // After OAuth redirect, restore initiating path + short polling for cookie
  useEffect(() => {
    const pending = localStorage.getItem('syncusive_post_login_path');
    if (!pending || me) return;
    setAuthChecking(true);
    let attempts = 0;
    const id = setInterval(async () => {
      attempts += 1;
      await fetchMe();
      if (me || attempts > 12) { // ~10s
        clearInterval(id);
        setAuthChecking(false);
        const target = pending;
        localStorage.removeItem('syncusive_post_login_path');
        if (window.location.pathname !== target) {
          window.history.replaceState({}, '', target);
          setPage(target);
        }
      }
    }, 800);
    return () => clearInterval(id);
  }, [fetchMe, me]);

  useEffect(() => {
    const onFocus = () => fetchMe();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchMe]);

  useEffect(() => {
    const handlePopState = () => setPage(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setPage(path);
  };

  const startGoogleLogin = () => {
    try {
      const current = window.location.pathname + window.location.search + window.location.hash;
      localStorage.setItem('syncusive_post_login_path', current);
    } catch {}
    // Use API base in local dev to avoid CRA HTML fallback on /api navigation
    const authStartUrl = API_BASE ? `${API_BASE}/api/auth/google/start` : `/api/auth/google/start`;
    window.location.assign(authStartUrl);
  };

  const startLogout = async () => {
    try {
      setAuthChecking(true);
      // Use the same API base logic as elsewhere; stay same-origin in prod
      const res = await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      // Optionally read the body (ignore errors)
      await res.clone().json().catch(() => {});
    } catch (e) {
      // Ignore network errors; we still clear local state and force a reload
    } finally {
      // Clear local user state immediately so UI switches to "Sign in"
      setMe(null);
      setAuthChecking(false);

      // Ensure server cookie invalidation is reflected across tabs:
      //  - If not on home, hard redirect to home with a marker
      //  - If already on home, do a hard reload
      const target = '/?logged=out';
      if (window.location.pathname !== '/') {
        window.location.replace(target);
      } else {
        window.location.reload();
      }
    }
  };

  const renderPage = () => {
    if (page === '/task-optimizer') return <TaskOptimizer />;
    if (page.startsWith('/blog/')) {
      const postId = page.split('/blog/')[1];
      return <BlogPostPage postId={postId} setPage={navigate} />;
    }
    if (page === '/blog') return <BlogIndexPage setPage={navigate} />;
    if (page === '/roadmap') return <RoadmapPage setPage={navigate} />;
    if (page === '/support') return <SupportPage setPage={navigate} />;
    if (page === '/tools') return <ToolsPage setPage={navigate} />;
    if (page === '/tools/pdf-editor') return <PDFEditorPage setPage={navigate} />;
    if (page === '/tools/image-converter') return <ImageConverterPage setPage={navigate} />;
    if (page === '/tools/json-formatter') return <JsonFormatterPage setPage={navigate} />;
    if (page === '/tools/text-case-converter') return <TextCaseConverterPage setPage={navigate} />;
    if (page === '/tools/password-generator') return <PasswordGeneratorPage setPage={navigate} />;
    if (page === '/tools/base64-tool') return <Base64ToolPage setPage={navigate} />;
    if (page === '/tools/pdf-toolkit') return <PdfToolkitPage setPage={navigate} />;
    if (page === '/tools/bg-upscale') return <BgUpscalePage setPage={navigate} />;
    if (page === '/tools/ocr') return <OcrToolPage setPage={navigate} />;
    if (page === '/tools/resume-ats') return <ResumeAtsPage setPage={navigate} />;
    if (page === '/tools/invoice-generator') return <InvoiceGeneratorPage setPage={navigate} />;
    if (page === '/tools/email-rewrite') return <EmailRewritePage setPage={navigate} />;
    if (page === '/tools/grammar-plagiarism') return <GrammarPlagiarismPage setPage={navigate} />;
    if (page === '/tools/url-toolkit') return <UrlToolkitPage setPage={navigate} />;
    if (page === '/tools/zip-unzip') return <ZipUnzipPage setPage={navigate} />;
    if (page === '/tools/qr-barcode') return <QrBarcodePage setPage={navigate} />;
    if (page.startsWith('/tools/')) return <ToolsPage setPage={navigate} />;
    return <LandingPage setPage={navigate} />;
  };

  return (
    <HelmetProvider>
    <div className="relative bg-gradient-to-br from-[#0a0a0a] via-[#111111] to-[#1a1a1a] min-h-screen text-white font-sans">
      <ScrollProgressBar />
      <CustomCursor />
      <Helmet>
        <title>Syncusive | AI-Powered Productivity Suite for Founders</title>
        <meta name="description" content="Syncusive is an AI-powered productivity suite..." />
        <link rel="icon" type="image/png" href="https://syncusivefiles.s3.ap-south-1.amazonaws.com/enhanced_logo.png" />
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            33% { transform: translateY(-10px) rotate(1deg); }
            66% { transform: translateY(5px) rotate(-1deg); }
          }
        `}</style>
        <script type="application/ld+json">
          {`{"@context":"https://schema.org","@type":"Organization","name":"Syncusive","url":"https://www.syncusive.com/","logo":"https://syncusivefiles.s3.ap-south-1.amazonaws.com/enhanced_logo.png","sameAs":["https://www.instagram.com/syncusive","https://x.com/syncusive"]}`}
        </script>
        <script type="application/ld+json">
          {`{"@context":"https://schema.org","@type":"WebSite","url":"https://www.syncusive.com/","potentialAction":{"@type":"SearchAction","target":{"@type":"EntryPoint","urlTemplate":"https://www.syncusive.com/search?q={search_term_string}"},"query-input":"required name=search_term_string"}}`}
        </script>
      </Helmet>

      <AnimatedLines />
      <FloatingParticles />

      <div className="relative z-10 min-h-screen flex flex-col">
        <Header
          setPage={navigate}
          me={me}
          onLogin={startGoogleLogin}
          onLogout={startLogout}
          authChecking={authChecking}
        />
        <FlickeringSeparator />
        {renderPage()}
        <Footer />
      </div>
    </div>
    </HelmetProvider>
  );
}